import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import RequestHistory from '../../components/RequestHistory';
import { 
  Briefcase, Plus, Loader2, Trash2, Eye, Building2, Calendar, FileText,
  CheckCircle, Clock, Archive, ClipboardCheck, AlertCircle, Ban, Edit,
  ArrowUp, ArrowDown, ArrowUpDown, Search, AlertTriangle, Upload, X, File, Download,
  DollarSign, Package, ListChecks, ShoppingCart, User, Send, MessageSquare, Inbox, XCircle, Users, Save, Forward,
  PackagePlus, Boxes, ArrowLeftRight, Info, Wrench, Receipt, GitCompareArrows
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const PurchasingJobs = ({ isBoard = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [correctionJobs, setCorrectionJobs] = useState([]);
  const [cancelledJobs, setCancelledJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [finalizedJobs, setFinalizedJobs] = useState([]); // Tamamlanan (onaylanan) işler
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [forwardedToManagementJobs, setForwardedToManagementJobs] = useState([]); // Yönetime iletilen
  const [approvedByManagementJobs, setApprovedByManagementJobs] = useState([]); // Yönetimin onayladığı
  const [loading, setLoading] = useState(true);
  
  // Atanan iş modal state'leri
  const [assignedDetailModalOpen, setAssignedDetailModalOpen] = useState(false);
  const [selectedAssignedJob, setSelectedAssignedJob] = useState(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completeLoading, setCompleteLoading] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLoading, setRejectLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [jobSubTab, setJobSubTab] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Talepler state'leri
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]); // Kullanıcının kendi talepleri
  const [taleplerSubTab, setTaleplerSubTab] = useState('gelen');
  const [selectedTalep, setSelectedTalep] = useState(null);
  const [talepActionLoading, setTalepActionLoading] = useState(false);
  const [talepActionNote, setTalepActionNote] = useState('');
  const [talepSortConfig, setTalepSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);
  const [managementCorrectionModalOpen, setManagementCorrectionModalOpen] = useState(false);
  const [correctionNote, setCorrectionNote] = useState('');
  const [convertToJobModalOpen, setConvertToJobModalOpen] = useState(false); // Onayla ve İşe Çevir modal
  const [prepareRequestModalOpen, setPrepareRequestModalOpen] = useState(false); // Talebi Hazırla modal
  const [selectedJob, setSelectedJob] = useState(null);
  
  // İptal Modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  // Teklif Karşılaştırma Modal state
  const [showJobCompareModal, setShowJobCompareModal] = useState(false);
  const [jobCompareOffers, setJobCompareOffers] = useState([]);
  const [jobCompareLoading, setJobCompareLoading] = useState(false);

  // Teklifleri API'den çekip karşılaştırma modalını aç
  const openJobCompare = async (teklifler) => {
    setShowJobCompareModal(true);
    setJobCompareLoading(true);
    try {
      const offerIds = teklifler.filter(t => t.offer_id).map(t => t.offer_id);
      const fetched = [];
      for (const id of offerIds) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/purchasing/offers/${id}`, { headers: getAuthHeaders() });
          if (res.ok) {
            const data = await res.json();
            fetched.push(data);
          }
        } catch (e) { /* skip */ }
      }
      setJobCompareOffers(fetched);
    } catch (e) {
      console.error('Teklif verileri çekilemedi:', e);
      setJobCompareOffers([]);
    }
    setJobCompareLoading(false);
  };
  
  // Yönetim Kurulu Onay Modal state
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalData, setApprovalData] = useState({
    selectedTeklifId: null,
    selectedTeklifIndex: null,
    selectedTeklif: null,
    aciklama: '',
    belgeler: []
  });
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  
  // Muhasebeye İlet Modal state
  const [forwardToAccountingModalOpen, setForwardToAccountingModalOpen] = useState(false);
  const [forwardToAccountingJob, setForwardToAccountingJob] = useState(null);
  const [forwardToAccountingNote, setForwardToAccountingNote] = useState('');
  const [forwardToAccountingSubmitting, setForwardToAccountingSubmitting] = useState(false);
  
  // Bilgilendirme Modal state
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoJob, setInfoJob] = useState(null);
  const [infoDescription, setInfoDescription] = useState('');
  const [infoFiles, setInfoFiles] = useState([]);
  const [infoSubmitting, setInfoSubmitting] = useState(false);
  
  // Yeni Hizmet Modal state
  const [showNewServiceModal, setShowNewServiceModal] = useState(false);
  const [newServiceJob, setNewServiceJob] = useState(null);
  const [newServiceData, setNewServiceData] = useState({
    service_name: '',
    description: '',
    cari_hesap_id: '',
    cari_hesap_name: '',
    branch_id: '',
    branch_name: '',
    start_date: '',
    end_date: '',
    total_amount: '',
    currency: 'TL',
    payment_type: 'pesin',
    notes: '',
    contract_documents: [],
    hakedisler: []
  });
  const [newServiceSubmitting, setNewServiceSubmitting] = useState(false);
  const newServiceFileInputRef = React.useRef(null);
  
  // Mevcut Teklifler listesi (Teklifler sayfasından)
  const [availableOffers, setAvailableOffers] = useState([]);
  const [usedOfferIds, setUsedOfferIds] = useState([]);
  const [offerSearchTerm, setOfferSearchTerm] = useState('');
  const [showOfferSelectModal, setShowOfferSelectModal] = useState(false);
  
  // Talebi Hazırla form state
  const [prepareRequestData, setPrepareRequestData] = useState({
    job_type: '', // urun_satin_alma, hizmet_satin_alma, diger
    tarih: '',
    saat: '',
    is_adi: '',
    aciklama: '',
    teklifler: []
  });
  const [prepareRequestSubmitting, setPrepareRequestSubmitting] = useState(false);
  
  // Form state for new job
  const [formData, setFormData] = useState({
    konu: '',
    aciklama: '',
    belgeler: [],
    sube_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Form state for correction
  const [correctionFormData, setCorrectionFormData] = useState({
    konu: '',
    aciklama: '',
    belgeler: []
  });
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  
  // Branches
  const [branches, setBranches] = useState([]);
  
  // Cari Hesaplar (Şirketler) - Yeni Hizmet Modalı için
  const [cariHesaplar, setCariHesaplar] = useState([]);
  
  // Sort config
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  // Check if job is unread by current user
  const isUnread = useCallback((job) => {
    if (!user?.id) return false;
    const readBy = job.read_by || [];
    return !readBy.includes(user.id);
  }, [user?.id]);

  // Mark single job as read
  const markAsRead = useCallback(async (jobId) => {
    if (!user?.id) return;
    try {
      await fetch(`${API}/purchasing/jobs/${jobId}/mark-read`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      // Update local state for all job arrays
      const updateReadBy = (jobList) => jobList.map(j => {
        if (j.id === jobId) {
          return { ...j, read_by: [...(j.read_by || []), user.id] };
        }
        return j;
      });
      setJobs(updateReadBy);
      setPendingJobs(updateReadBy);
      setCorrectionJobs(updateReadBy);
      setCompletedJobs(updateReadBy);
      setForwardedToManagementJobs(updateReadBy);
      setApprovedByManagementJobs(updateReadBy);
    } catch (err) {
      console.error('Okundu işaretleme hatası:', err);
    }
  }, [user?.id]);

  // Load branches
  const loadBranches = useCallback(async () => {
    try {
      const response = await fetch(`${API}/branches`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    }
  }, []);

  // Load active jobs
  const loadActiveJobs = useCallback(async () => {
    try {
      // Active jobs = approved + prepared + to_complete
      const [approvedRes, preparedRes, toCompleteRes] = await Promise.all([
        fetch(`${API}/purchasing/jobs/approved`, { headers: getAuthHeaders() }),
        fetch(`${API}/purchasing/jobs/prepared`, { headers: getAuthHeaders() }),
        fetch(`${API}/purchasing/jobs/to_complete`, { headers: getAuthHeaders() })
      ]);
      
      let allJobs = [];
      if (approvedRes.ok) {
        const approved = await approvedRes.json();
        allJobs = [...allJobs, ...approved];
      }
      if (preparedRes.ok) {
        const prepared = await preparedRes.json();
        allJobs = [...allJobs, ...prepared];
      }
      if (toCompleteRes.ok) {
        const toComplete = await toCompleteRes.json();
        allJobs = [...allJobs, ...toComplete];
      }
      setJobs(allJobs);
    } catch (error) {
      console.error('Aktif işler yüklenirken hata:', error);
    }
  }, []);

  // Load available offers for selection
  const loadAvailableOffers = useCallback(async () => {
    try {
      const [offersRes, usedIdsRes] = await Promise.all([
        fetch(`${API}/purchasing/offers`, { headers: getAuthHeaders() }),
        fetch(`${API}/purchasing/offers/used-ids`, { headers: getAuthHeaders() })
      ]);
      if (offersRes.ok) {
        const data = await offersRes.json();
        setAvailableOffers(data);
      }
      if (usedIdsRes.ok) {
        const usedData = await usedIdsRes.json();
        setUsedOfferIds(usedData.used_offer_ids || []);
      }
    } catch (error) {
      console.error('Teklifler yüklenirken hata:', error);
    }
  }, []);

  // Yeni Hizmet fonksiyonları
  const resetNewServiceData = () => {
    setNewServiceData({
      service_name: '',
      description: '',
      cari_hesap_id: '',
      cari_hesap_name: '',
      branch_id: '',
      branch_name: '',
      start_date: '',
      end_date: '',
      total_amount: '',
      currency: 'TL',
      payment_type: 'pesin',
      notes: '',
      contract_documents: [],
      hakedisler: []
    });
  };

  const handleNewServiceFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/upload/offers`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setNewServiceData(prev => ({
        ...prev,
        contract_documents: [...prev.contract_documents, ...response.data.files]
      }));
      toast.success('Belge yüklendi');
    } catch (err) {
      console.error('Dosya yüklenirken hata:', err);
      toast.error('Dosya yüklenemedi');
    }
    
    e.target.value = '';
  };

  const removeNewServiceDocument = (index) => {
    setNewServiceData(prev => ({
      ...prev,
      contract_documents: prev.contract_documents.filter((_, i) => i !== index)
    }));
  };

  const addNewServiceHakedis = () => {
    setNewServiceData(prev => ({
      ...prev,
      hakedisler: [
        ...prev.hakedisler,
        {
          id: Date.now(),
          hakedis_no: prev.hakedisler.length + 1,
          tarih: new Date().toISOString().split('T')[0],
          miktar: '',
          tutar: '',
          aciklama: '',
          belgeler: [],
          durum: 'beklemede'
        }
      ]
    }));
  };

  const updateNewServiceHakedis = (hakedisId, field, value) => {
    setNewServiceData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.map(h => 
        h.id === hakedisId ? { ...h, [field]: value } : h
      )
    }));
  };

  const removeNewServiceHakedis = (hakedisId) => {
    setNewServiceData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.filter(h => h.id !== hakedisId)
    }));
  };

  // Hakediş belgesi yükle (Yeni Hizmet Modal için)
  const handleNewServiceHakedisFileUpload = async (e, hakedisId) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/upload/offers`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setNewServiceData(prev => ({
        ...prev,
        hakedisler: prev.hakedisler.map(h => 
          h.id === hakedisId 
            ? { ...h, belgeler: [...h.belgeler, ...response.data.files] }
            : h
        )
      }));
      toast.success('Hakediş belgesi yüklendi');
    } catch (err) {
      console.error('Dosya yüklenirken hata:', err);
      toast.error('Dosya yüklenemedi');
    }
    
    e.target.value = '';
  };

  // Hakediş belgesi sil
  const removeNewServiceHakedisDocument = (hakedisId, docIndex) => {
    setNewServiceData(prev => ({
      ...prev,
      hakedisler: prev.hakedisler.map(h => 
        h.id === hakedisId 
          ? { ...h, belgeler: h.belgeler.filter((_, i) => i !== docIndex) }
          : h
      )
    }));
  };

  const handleSaveNewService = async () => {
    if (!newServiceData.service_name || !newServiceData.total_amount) {
      toast.error('Lütfen hizmet adı ve tutarı doldurun');
      return;
    }

    setNewServiceSubmitting(true);
    try {
      const serviceData = {
        ...newServiceData,
        source_job_id: newServiceJob?.id,
        source_job_name: newServiceJob?.amac || newServiceJob?.konu || ''
      };
      
      await axios.post(`${API}/purchasing/services`, serviceData, { headers: getAuthHeaders() });
      toast.success('Hizmet başarıyla kaydedildi');
      setShowNewServiceModal(false);
      resetNewServiceData();
      setNewServiceJob(null);
    } catch (err) {
      console.error('Hizmet kaydedilirken hata:', err);
      toast.error('Hizmet kaydedilemedi');
    } finally {
      setNewServiceSubmitting(false);
    }
  };

  const currencySymbols = { 'TL': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£' };

  // Load pending jobs
  const loadPendingJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/pending`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setPendingJobs(data);
      }
    } catch (error) {
      console.error('Bekleyen işler yüklenirken hata:', error);
    }
  }, []);

  // Load correction jobs
  const loadCorrectionJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/correction`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCorrectionJobs(data);
      }
    } catch (error) {
      console.error('Düzeltme işleri yüklenirken hata:', error);
    }
  }, []);

  // Load cancelled jobs (rejected)
  const loadCancelledJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/rejected`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCancelledJobs(data);
      }
    } catch (error) {
      console.error('İptal edilen işler yüklenirken hata:', error);
    }
  }, []);

  // Load completed jobs (Hazırlanan İşler)
  const loadCompletedJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/completed`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCompletedJobs(data);
      }
    } catch (error) {
      console.error('Hazırlanan işler yüklenirken hata:', error);
    }
  }, []);

  // Load finalized jobs (Tamamlanan - onaylanan işler)
  const loadFinalizedJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/finalized`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setFinalizedJobs(data);
      }
    } catch (error) {
      console.error('Tamamlanan işler yüklenirken hata:', error);
    }
  }, []);

  // Load assigned jobs (Bana Atanan İşler)
  const loadAssignedJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/my-assigned-jobs`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setAssignedJobs(data);
      }
    } catch (error) {
      console.error('Atanan işler yüklenirken hata:', error);
    }
  }, []);

  // Load archived jobs (status = archived)
  const loadArchivedJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/archived`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setArchivedJobs(data);
      }
    } catch (error) {
      console.error('Arşiv işleri yüklenirken hata:', error);
    }
  }, []);

  // Yönetime iletilen işleri yükle
  const loadForwardedToManagementJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/forwarded-to-management`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setForwardedToManagementJobs(data);
      }
    } catch (error) {
      console.error('Yönetime iletilen işler yüklenirken hata:', error);
    }
  }, []);

  // Yönetimin onayladığı işleri yükle
  const loadApprovedByManagementJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/approved-by-management`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setApprovedByManagementJobs(data);
      }
    } catch (error) {
      console.error('Yönetimin onayladığı işler yüklenirken hata:', error);
    }
  }, []);

  // Load incoming requests (talepler)
  const loadIncomingRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/purchasing/incoming-requests-v2`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setIncomingRequests(data);
      }
    } catch (error) {
      console.error('Talepler yüklenirken hata:', error);
    }
  }, []);

  // Kullanıcının kendi taleplerini yükle
  const loadMyRequests = useCallback(async () => {
    try {
      const response = await fetch(`${API}/work/requests/my`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setMyRequests(data);
      }
    } catch (error) {
      console.error('Taleplerim yüklenirken hata:', error);
    }
  }, []);

  // İşleri okundu olarak işaretle
  const markJobsAsRead = useCallback(async () => {
    try {
      await fetch(`${API}/purchasing/jobs/mark-read`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      });
    } catch (error) {
      console.error('İşler okundu işaretlenirken hata:', error);
    }
  }, []);

  // Cari Hesapları yükle (Yeni Hizmet Modalı için)
  const fetchCariHesaplar = useCallback(async () => {
    try {
      const response = await fetch(`${API}/global/cari-hesaplar`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setCariHesaplar(data || []);
      }
    } catch (error) {
      console.error('Cari hesaplar yüklenirken hata:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadBranches(),
        loadActiveJobs(),
        loadPendingJobs(),
        loadCorrectionJobs(),
        loadCancelledJobs(),
        loadCompletedJobs(),
        loadFinalizedJobs(),
        loadArchivedJobs(),
        loadForwardedToManagementJobs(),
        loadApprovedByManagementJobs(),
        loadIncomingRequests(),
        loadMyRequests(),
        loadAssignedJobs(),
        loadAvailableOffers(),
        fetchCariHesaplar()
      ]);
      // İşler yüklendikten sonra okundu olarak işaretle
      await markJobsAsRead();
      setLoading(false);
    };
    loadAll();
  }, [loadBranches, loadActiveJobs, loadPendingJobs, loadCorrectionJobs, loadCancelledJobs, loadCompletedJobs, loadFinalizedJobs, loadArchivedJobs, loadForwardedToManagementJobs, loadApprovedByManagementJobs, loadIncomingRequests, markJobsAsRead, loadAssignedJobs, loadAvailableOffers, fetchCariHesaplar]);

  // Gerçek zamanlı: veri değişince iş listelerini otomatik yenile
  useRealtimeRefresh(() => {
    loadActiveJobs();
    loadPendingJobs();
    loadForwardedToManagementJobs();
    loadApprovedByManagementJobs();
    loadIncomingRequests();
    loadMyRequests();
    loadAssignedJobs();
  });

  // File upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newBelgeler = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newBelgeler.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: base64
      });
    }
    
    setFormData(prev => ({
      ...prev,
      belgeler: [...prev.belgeler, ...newBelgeler]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // Submit new job
  const handleSubmit = async () => {
    if (!formData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/purchasing/jobs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('İş başarıyla eklendi - Onay bekleyenlere eklendi');
        setAddModalOpen(false);
        setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
        loadPendingJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İş eklenemedi');
      }
    } catch (error) {
      console.error('İş ekleme hatası:', error);
      toast.error('İş eklenemedi');
    }
    setSubmitting(false);
  };

  // Get job status
  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Onay Bekliyor', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      active: { label: 'Devam Ediyor', color: 'bg-emerald-100 text-emerald-700', icon: Briefcase },
      approved: { label: 'Devam Ediyor', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      completed: { label: 'Hazırlandı', color: 'bg-blue-100 text-blue-700', icon: Clock },
      finalized: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      correction: { label: 'Düzeltme', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
      cancelled: { label: 'İptal', color: 'bg-red-100 text-red-700', icon: Ban },
      rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', icon: XCircle },
      archived: { label: 'Arşivlendi', color: 'bg-gray-100 text-gray-700', icon: Archive }
    };
    return configs[status] || configs.pending;
  };

  // Talebi Hazırla - Modal Aç
  const openPrepareRequestModal = (job) => {
    const now = new Date();
    const tarih = now.toISOString().split('T')[0];
    const saat = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    
    // Eğer işte kaydedilmiş taslak varsa onu yükle
    if (job.draft_request) {
      const draft = job.draft_request;
      setPrepareRequestData({
        job_type: draft.job_type || job.job_type || '',
        tarih: draft.tarih || tarih,
        saat: draft.saat || saat,
        is_adi: draft.is_adi || job.konu || job.amac || '',
        aciklama: draft.aciklama || job.aciklama || '',
        teklifler: (draft.teklifler || []).map((t, index) => ({
          id: Date.now() + index,
          firma_adi: t.firma_adi || '',
          toplam_fiyat: t.toplam_fiyat || '',
          odeme_plani: t.odeme_plani || '',
          aciklama: t.aciklama || '',
          belgeler: t.belgeler || []
        }))
      });
    } else {
      setPrepareRequestData({
        job_type: job.job_type || '',
        tarih: tarih,
        saat: saat,
        is_adi: job.konu || job.amac || '',
        aciklama: job.aciklama || '',
        teklifler: []
      });
    }
    setPrepareRequestModalOpen(true);
  };

  // Teklif Ekle (Manuel)
  const addTeklif = () => {
    setPrepareRequestData(prev => ({
      ...prev,
      teklifler: [
        ...prev.teklifler,
        {
          id: Date.now(),
          firma_adi: '',
          toplam_fiyat: '',
          odeme_plani: '',
          aciklama: '',
          belgeler: [],
          from_existing: false // Manuel eklendi
        }
      ]
    }));
  };

  // Mevcut Tekliften Ekle (Teklifler listesinden)
  const addTeklifFromExisting = (offer) => {
    // Toplam fiyatı hesapla
    const itemsTotal = (offer.items || []).reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const servicesTotal = (offer.services || []).reduce((sum, srv) => sum + (srv.quantity * srv.price), 0);
    const totalPrice = itemsTotal + servicesTotal;
    
    // Para birimi sembolü
    const currencySymbols = { 'TL': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£' };
    const symbol = currencySymbols[offer.currency] || '₺';
    
    // Ödeme planı detaylı metni oluştur
    let paymentPlanText = '';
    let paymentDetails = [];
    
    const paymentType = offer.payment_type || 'belirtilmemis';
    
    if (paymentType === 'pesin') {
      paymentPlanText = 'Peşin';
    } else if (paymentType === 'cek') {
      const checkCount = offer.check_count || 0;
      paymentPlanText = checkCount > 0 ? `Çek (${checkCount} adet)` : 'Çek';
      
      // Çek detayları
      if (offer.checks && offer.checks.length > 0) {
        paymentDetails = offer.checks.map((check, idx) => {
          const date = check.date ? new Date(check.date).toLocaleDateString('tr-TR') : '-';
          const amount = check.amount ? `${parseFloat(check.amount).toLocaleString('tr-TR')} ${symbol}` : '-';
          return `${idx + 1}. Çek: ${date} - ${amount}`;
        });
      }
    } else if (paymentType === 'vadeli') {
      const installmentCount = offer.installment_count || 0;
      paymentPlanText = installmentCount > 0 ? `Vadeli (${installmentCount} taksit)` : 'Vadeli';
      
      // Taksit detayları
      if (offer.installments && offer.installments.length > 0) {
        paymentDetails = offer.installments.map((inst, idx) => {
          const date = inst.date ? new Date(inst.date).toLocaleDateString('tr-TR') : '-';
          const amount = inst.amount ? `${parseFloat(inst.amount).toLocaleString('tr-TR')} ${symbol}` : '-';
          return `${idx + 1}. Taksit: ${date} - ${amount}`;
        });
      }
    } else {
      // Ödeme planı belirtilmemiş - eski teklifler için
      paymentPlanText = '⚠️ Ödeme planı belirtilmemiş (Teklifte tanımlanmamış)';
    }
    
    // Ödeme planı ve detayları birleştir
    const fullPaymentPlan = paymentDetails.length > 0 
      ? `${paymentPlanText}\n${paymentDetails.join('\n')}`
      : paymentPlanText;
    
    // Açıklama metnini oluştur - ürün/hizmet ve ödeme detayları dahil
    let descriptionParts = [];
    
    // Mevcut açıklama
    if (offer.notes) {
      descriptionParts.push(offer.notes);
    }
    
    // Ürün listesi
    if (offer.items && offer.items.length > 0) {
      const itemsList = offer.items.map(item => 
        `• ${item.product_name}: ${item.quantity} ${item.unit} x ${item.price.toLocaleString('tr-TR')} ${symbol}`
      ).join('\n');
      descriptionParts.push(`\n📦 Ürünler:\n${itemsList}`);
    }
    
    // Hizmet listesi
    if (offer.services && offer.services.length > 0) {
      const servicesList = offer.services.map(srv => 
        `• ${srv.service_name}: ${srv.quantity} ${srv.unit} x ${srv.price.toLocaleString('tr-TR')} ${symbol}`
      ).join('\n');
      descriptionParts.push(`\n🔧 Hizmetler:\n${servicesList}`);
    }
    
    // Ödeme detayları açıklamaya ekle (sadece detay varsa)
    if (paymentDetails.length > 0) {
      descriptionParts.push(`\n💳 Ödeme Detayları:\n${paymentDetails.join('\n')}`);
    }
    
    const fullDescription = descriptionParts.join('\n');
    
    setPrepareRequestData(prev => ({
      ...prev,
      teklifler: [
        ...prev.teklifler,
        {
          id: Date.now(),
          offer_id: offer.id, // Mevcut teklif referansı
          offer_no: offer.offer_no, // Teklif numarası
          firma_adi: offer.cari_hesap_name || '',
          toplam_fiyat: `${totalPrice.toLocaleString('tr-TR')} ${symbol}`,
          odeme_plani: fullPaymentPlan,
          aciklama: fullDescription,
          belgeler: offer.documents || [],
          from_existing: true, // Mevcut tekliften eklendi
          original_offer: offer, // Orijinal teklif verisi
          // Ödeme detayları ayrıca sakla
          payment_type: offer.payment_type,
          currency: offer.currency,
          checks: offer.checks || [],
          installments: offer.installments || []
        }
      ]
    }));
    
    setShowOfferSelectModal(false);
    setOfferSearchTerm('');
    toast.success(`${offer.offer_no} numaralı teklif eklendi`);
    
    // Teklif durumunu "secimdeki" yap (Teklif Listesi'nden kaldır)
    fetch(`${API}/purchasing/offers/${offer.id}/status`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status: 'secimdeki' })
    }).catch(err => console.error('Teklif durumu güncellenemedi:', err));
  };

  // Filtrelenmiş teklifler (arama için)
  const filteredAvailableOffers = useMemo(() => {
    // Mevcut işteki tekliflerin offer_id'lerini çıkar
    const currentJobOfferIds = prepareRequestData.teklifler
      .filter(t => t.offer_id)
      .map(t => t.offer_id);
    
    // Kullanılmış ve mevcut işteki teklifleri filtrele
    let filtered = availableOffers.filter(offer => 
      !usedOfferIds.includes(offer.id) && !currentJobOfferIds.includes(offer.id)
    );
    
    // Arama filtresi
    if (offerSearchTerm) {
      const searchLower = offerSearchTerm.toLowerCase();
      filtered = filtered.filter(offer => 
        (offer.offer_no || '').toLowerCase().includes(searchLower) ||
        (offer.cari_hesap_name || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [availableOffers, offerSearchTerm, usedOfferIds, prepareRequestData.teklifler]);

  // Teklif Sil
  const removeTeklif = (teklifId) => {
    setPrepareRequestData(prev => ({
      ...prev,
      teklifler: prev.teklifler.filter(t => t.id !== teklifId)
    }));
  };

  // Teklif Güncelle
  const updateTeklif = (teklifId, field, value) => {
    setPrepareRequestData(prev => ({
      ...prev,
      teklifler: prev.teklifler.map(t => 
        t.id === teklifId ? { ...t, [field]: value } : t
      )
    }));
  };

  // Teklif Belge Yükle
  const handleTeklifFileUpload = async (e, teklifId) => {
    const files = Array.from(e.target.files);
    const newBelgeler = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newBelgeler.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: base64
      });
    }
    
    setPrepareRequestData(prev => ({
      ...prev,
      teklifler: prev.teklifler.map(t => 
        t.id === teklifId ? { ...t, belgeler: [...t.belgeler, ...newBelgeler] } : t
      )
    }));
  };

  // Teklif Belge Sil
  const removeTeklifFile = (teklifId, fileIndex) => {
    setPrepareRequestData(prev => ({
      ...prev,
      teklifler: prev.teklifler.map(t => 
        t.id === teklifId ? { ...t, belgeler: t.belgeler.filter((_, i) => i !== fileIndex) } : t
      )
    }));
  };

  // Talebi Hazırla - Kaydet
  const handleSubmitPrepareRequest = async () => {
    if (!prepareRequestData.job_type) {
      toast.error('İş türü seçmelisiniz');
      return;
    }
    
    if (prepareRequestData.teklifler.length === 0) {
      toast.error('En az bir teklif eklemelisiniz');
      return;
    }
    
    // Teklif validasyonu
    for (let i = 0; i < prepareRequestData.teklifler.length; i++) {
      const teklif = prepareRequestData.teklifler[i];
      if (!teklif.firma_adi.trim()) {
        toast.error(`${i + 1}. teklif için firma adı zorunludur`);
        return;
      }
      if (!teklif.toplam_fiyat) {
        toast.error(`${i + 1}. teklif için toplam fiyat zorunludur`);
        return;
      }
    }
    
    setPrepareRequestSubmitting(true);
    try {
      const response = await fetch(`${API}/purchasing/jobs/${selectedJob.id}/prepare-request`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          job_type: prepareRequestData.job_type,
          tarih: prepareRequestData.tarih,
          saat: prepareRequestData.saat,
          is_adi: prepareRequestData.is_adi,
          aciklama: prepareRequestData.aciklama,
          teklifler: prepareRequestData.teklifler.map((t, index) => ({
            sira: index + 1,
            offer_id: t.offer_id || null,
            offer_no: t.offer_no || null,
            firma_adi: t.firma_adi,
            toplam_fiyat: t.toplam_fiyat,
            odeme_plani: t.odeme_plani,
            aciklama: t.aciklama,
            belgeler: t.belgeler
          }))
        })
      });
      
      if (response.ok) {
        toast.success('Talep hazırlandı ve yönetime iletildi');
        setPrepareRequestModalOpen(false);
        setDetailModalOpen(false);
        setPrepareRequestData({ job_type: '', tarih: '', saat: '', is_adi: '', aciklama: '', teklifler: [] });
        loadActiveJobs();
        loadForwardedToManagementJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Talep hazırlanamadı');
      }
    } catch (error) {
      console.error('Talep hazırlama hatası:', error);
      toast.error('Talep hazırlanamadı');
    }
    setPrepareRequestSubmitting(false);
  };

  // Talebi Hazırla - Taslak Olarak Kaydet
  const handleSavePrepareRequestDraft = async () => {
    setPrepareRequestSubmitting(true);
    try {
      const response = await fetch(`${API}/purchasing/jobs/${selectedJob.id}/save-request-draft`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          job_type: prepareRequestData.job_type,
          tarih: prepareRequestData.tarih,
          saat: prepareRequestData.saat,
          is_adi: prepareRequestData.is_adi,
          aciklama: prepareRequestData.aciklama,
          teklifler: prepareRequestData.teklifler.map((t, index) => ({
            sira: index + 1,
            firma_adi: t.firma_adi,
            toplam_fiyat: t.toplam_fiyat,
            odeme_plani: t.odeme_plani,
            aciklama: t.aciklama,
            belgeler: t.belgeler
          }))
        })
      });
      
      if (response.ok) {
        toast.success('Taslak kaydedildi');
        // Modal'ı kapat
        setPrepareRequestModalOpen(false);
        // selectedJob'u güncelle (draft_request eklenmiş hali için)
        setSelectedJob(prev => ({
          ...prev,
          job_type: prepareRequestData.job_type,
          draft_request: {
            job_type: prepareRequestData.job_type,
            tarih: prepareRequestData.tarih,
            saat: prepareRequestData.saat,
            is_adi: prepareRequestData.is_adi,
            aciklama: prepareRequestData.aciklama,
            teklifler: prepareRequestData.teklifler.map((t, index) => ({
              sira: index + 1,
              offer_id: t.offer_id || null,
              offer_no: t.offer_no || null,
              firma_adi: t.firma_adi,
              toplam_fiyat: t.toplam_fiyat,
              odeme_plani: t.odeme_plani,
              aciklama: t.aciklama,
              belgeler: t.belgeler
            }))
          }
        }));
        // İşleri yeniden yükle
        loadActiveJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Taslak kaydedilemedi');
      }
    } catch (error) {
      console.error('Taslak kaydetme hatası:', error);
      toast.error('Taslak kaydedilemedi');
    }
    setPrepareRequestSubmitting(false);
  };

  // Sipariş ekleme - Alınan Ürünler sayfasına yönlendir
  const openCompleteOrderModal = (job) => {
    // İş bilgilerini localStorage'a kaydet
    localStorage.setItem('pending_order_from_job', JSON.stringify({
      job_id: job.id,
      sube_id: job.sube_id,
      branch_name: job.branch_name,
      konu: job.konu || job.amac,
      selected_teklif: job.selected_teklif
    }));
    
    // Alınan Ürünler sayfasına yönlendir (modal otomatik açılacak)
    navigate('/purchasing/purchased?openAddModal=true');
  };

  // Handle approve
  // Handle approve - Basit onay (Onay Bekleyen → Devam Eden)
  const handleApprove = async (job) => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İş onaylandı - Devam Eden\'e taşındı');
        loadPendingJobs();
        loadActiveJobs();
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Onaylama başarısız');
      }
    } catch (error) {
      console.error('Onaylama hatası:', error);
      toast.error('Onaylama başarısız');
    }
  };

  // Handle approval submit (Yönetime İletilen → Yönetim Onayladı için detaylı modal)
  const handleApprovalSubmit = async () => {
    if (!selectedJob) return;
    
    const teklifler = selectedJob.prepared_request?.teklifler || [];
    
    // Eğer teklif varsa, bir teklif seçilmeli
    if (teklifler.length > 0 && !approvalData.selectedTeklifId && approvalData.selectedTeklifIndex === null) {
      toast.error('Lütfen bir teklif seçin');
      return;
    }
    
    setApprovalSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('selected_teklif_id', approvalData.selectedTeklifId || '');
      formData.append('selected_teklif_index', approvalData.selectedTeklifIndex?.toString() || '');
      if (approvalData.selectedTeklif) {
        formData.append('selected_teklif', JSON.stringify(approvalData.selectedTeklif));
      }
      formData.append('aciklama', approvalData.aciklama || '');
      
      // Belgeleri ekle
      for (const file of approvalData.belgeler) {
        formData.append('belgeler', file);
      }
      
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${API}/purchasing/jobs/${selectedJob.id}/approve-with-details`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        toast.success('İş onaylandı - Yönetim Onayladı\'ya taşındı');
        setApprovalModalOpen(false);
        setApprovalData({ selectedTeklifId: null, selectedTeklifIndex: null, selectedTeklif: null, aciklama: '', belgeler: [] });
        loadForwardedToManagementJobs();
        loadApprovedByManagementJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Onaylama başarısız');
      }
    } catch (error) {
      console.error('Onaylama hatası:', error);
      toast.error('Onaylama başarısız');
    } finally {
      setApprovalSubmitting(false);
    }
  };

  // Handle forward to accounting (Muhasebeye İlet)
  const handleForwardToAccounting = async () => {
    if (!forwardToAccountingJob) return;
    
    setForwardToAccountingSubmitting(true);
    try {
      const response = await fetch(`${API}/purchasing/jobs/${forwardToAccountingJob.id}/forward-to-accounting`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          aciklama: forwardToAccountingNote.trim()
        })
      });
      
      if (response.ok) {
        toast.success('İş başarıyla muhasebeye iletildi');
        setForwardToAccountingModalOpen(false);
        setForwardToAccountingJob(null);
        setForwardToAccountingNote('');
        setDetailModalOpen(false);
        // Tamamlanan işleri yeniden yükle
        loadCompletedJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Muhasebeye iletme başarısız');
      }
    } catch (error) {
      console.error('Muhasebeye iletme hatası:', error);
      toast.error('Muhasebeye iletme başarısız');
    } finally {
      setForwardToAccountingSubmitting(false);
    }
  };

  // Handle reject
  const handleReject = async (job) => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/reject`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İş reddedildi');
        loadPendingJobs();
        loadCancelledJobs();
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Reddetme başarısız');
      }
    } catch (error) {
      console.error('Reddetme hatası:', error);
      toast.error('Reddetme başarısız');
    }
  };

  // Handle cancel job (İptal Et)
  const handleCancelJob = async () => {
    if (!selectedJob) return;
    
    setCancelSubmitting(true);
    try {
      const response = await fetch(`${API}/purchasing/jobs/${selectedJob.id}/cancel`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cancel_reason: cancelReason
        })
      });
      
      if (response.ok) {
        toast.success('İş iptal edildi');
        loadPendingJobs();
        loadActiveJobs();
        loadCancelledJobs();
        setCancelModalOpen(false);
        setDetailModalOpen(false);
        setCancelReason('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İptal işlemi başarısız');
      }
    } catch (error) {
      console.error('İptal hatası:', error);
      toast.error('İptal işlemi başarısız');
    }
    setCancelSubmitting(false);
  };

  // Handle forward to management (Yönetime İlet)
  const handleForwardToManagement = async (job) => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/forward-to-management`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İş yönetime iletildi');
        loadPendingJobs();
        loadForwardedToManagementJobs();
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Yönetime iletme başarısız');
      }
    } catch (error) {
      console.error('Yönetime iletme hatası:', error);
      toast.error('Yönetime iletme başarısız');
    }
  };

  // Handle management approve (Yönetim onayı)
  // Handle management approve - Detaylı modal açar (Yönetime İletilen → Yönetim Onayladı)
  const handleManagementApprove = async (job) => {
    // Detaylı modal'ı aç
    setSelectedJob(job);
    setApprovalData({
      selectedTeklifId: null,
      selectedTeklifIndex: null,
      selectedTeklif: null,
      aciklama: '',
      belgeler: []
    });
    setApprovalModalOpen(true);
  };

  // Handle management correction request (Yönetim düzeltme isteği)
  const handleManagementCorrection = async () => {
    if (!correctionNote.trim()) {
      toast.error('Düzeltme notu gerekli');
      return;
    }
    
    try {
      const response = await fetch(`${API}/board/purchasing-jobs/${selectedJob.id}/request-correction`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: correctionNote })
      });
      
      if (response.ok) {
        toast.success('Düzeltme istendi');
        loadForwardedToManagementJobs();
        loadCorrectionJobs();
        setCorrectionModalOpen(false);
        setCorrectionNote('');
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Düzeltme isteği başarısız');
      }
    } catch (error) {
      console.error('Düzeltme isteği hatası:', error);
      toast.error('Düzeltme isteği başarısız');
    }
  };

  // Handle correction request
  const handleRequestCorrection = async (job) => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/correction`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Düzeltme istendi');
        loadPendingJobs();
        loadCorrectionJobs();
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Düzeltme isteği başarısız');
      }
    } catch (error) {
      console.error('Düzeltme isteği hatası:', error);
      toast.error('Düzeltme isteği başarısız');
    }
  };

  // Handle complete
  const handleComplete = async (job) => {
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/complete`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İş tamamlandı');
        loadActiveJobs();
        loadCompletedJobs();
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Tamamlama başarısız');
      }
    } catch (error) {
      console.error('Tamamlama hatası:', error);
      toast.error('Tamamlama başarısız');
    }
  };

  // Handle finalize - move from Hazırlanan to Tamamlanan
  const handleFinalize = async (job) => {
    if (!window.confirm('Bu işi onaylayıp tamamlamak istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/finalize`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ note: 'İş onaylandı ve tamamlandı' })
      });
      
      if (response.ok) {
        toast.success('İş onaylandı ve tamamlandı');
        loadCompletedJobs();
        loadFinalizedJobs();
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Onaylama başarısız');
      }
    } catch (error) {
      console.error('Onaylama hatası:', error);
      toast.error('Onaylama başarısız');
    }
  };

  // Handle archive - move finalized job to archive
  const handleArchiveJob = async (job) => {
    if (!window.confirm('Bu işi arşive taşımak istediğinizden emin misiniz?')) return;
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/archive`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('İş arşive taşındı');
        loadFinalizedJobs();
        loadArchivedJobs();
        setDetailModalOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Arşivleme başarısız');
      }
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error('Arşivleme başarısız');
    }
  };

  // Handle action button click - mark job as completed and navigate
  const handleActionAndNavigate = async (job, actionType, navigatePath) => {
    try {
      // İşi "completed" (Hazırlanan) durumuna güncelle
      const response = await fetch(`${API}/purchasing/jobs/${job.id}/mark-prepared`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          action_type: actionType,
          note: `${actionType} işlemi başlatıldı`
        })
      });
      
      if (response.ok) {
        toast.success('İş hazırlanan işlere taşındı');
        loadApprovedByManagementJobs();
        loadCompletedJobs();
        setDetailModalOpen(false);
        // Sayfaya yönlendir
        navigate(navigatePath);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('İşlem hatası:', error);
      toast.error('İşlem başarısız');
    }
  };

  // Handle delete
  const handleDelete = async (job) => {
    if (!window.confirm('Bu işi silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API}/purchasing/jobs/${job.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('İş silindi');
        loadActiveJobs();
        loadPendingJobs();
        loadCorrectionJobs();
        setDetailModalOpen(false);
      } else {
        toast.error('İş silinemedi');
      }
    } catch (error) {
      console.error('İş silme hatası:', error);
      toast.error('İş silinemedi');
    }
  };

  // Open correction modal
  const openCorrectionModal = (job) => {
    setSelectedJob(job);
    setCorrectionFormData({
      konu: job.konu || '',
      aciklama: '',
      belgeler: []
    });
    setDetailModalOpen(false);
    setCorrectionModalOpen(true);
  };

  // File upload for correction
  const handleCorrectionFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newBelgeler = [];
    
    for (const file of files) {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newBelgeler.push({
        name: file.name,
        size: file.size,
        type: file.type,
        url: base64
      });
    }
    
    setCorrectionFormData(prev => ({
      ...prev,
      belgeler: [...prev.belgeler, ...newBelgeler]
    }));
  };

  const removeCorrectionFile = (index) => {
    setCorrectionFormData(prev => ({
      ...prev,
      belgeler: prev.belgeler.filter((_, i) => i !== index)
    }));
  };

  // Submit correction
  const handleSubmitCorrection = async () => {
    if (!correctionFormData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }
    
    setCorrectionSubmitting(true);
    try {
      const response = await fetch(`${API}/purchasing/jobs/${selectedJob.id}/submit-correction`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          konu: correctionFormData.konu,
          aciklama: correctionFormData.aciklama,
          belgeler: correctionFormData.belgeler
        })
      });
      
      if (response.ok) {
        toast.success('Düzeltme gönderildi - İş tekrar onay bekleyenlere eklendi');
        setCorrectionModalOpen(false);
        setCorrectionFormData({ konu: '', aciklama: '', belgeler: [] });
        setSelectedJob(null);
        setActiveTab('active');
        setJobSubTab('pending');
        loadPendingJobs();
        loadCorrectionJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Düzeltme gönderilemedi');
      }
    } catch (error) {
      console.error('Düzeltme gönderme hatası:', error);
      toast.error('Düzeltme gönderilemedi');
    }
    setCorrectionSubmitting(false);
  };

  // Handle assigned job complete
  const handleCompleteAssignedJob = async () => {
    setCompleteLoading(true);
    try {
      const response = await fetch(`${API}/purchasing/assigned-jobs/${selectedAssignedJob.id}/complete`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes: completeNotes })
      });
      
      if (response.ok) {
        toast.success('İş başarıyla tamamlandı');
        setCompleteModalOpen(false);
        setAssignedDetailModalOpen(false);
        setCompleteNotes('');
        loadAssignedJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İş tamamlanamadı');
      }
    } catch (error) {
      console.error('İş tamamlama hatası:', error);
      toast.error('İş tamamlanamadı');
    }
    setCompleteLoading(false);
  };

  // Handle assigned job reject
  const handleRejectAssignedJob = async () => {
    if (!rejectReason.trim()) {
      toast.error('Lütfen red sebebi girin');
      return;
    }
    
    setRejectLoading(true);
    try {
      const response = await fetch(`${API}/purchasing/assigned-jobs/${selectedAssignedJob.id}/reject`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: rejectReason })
      });
      
      if (response.ok) {
        toast.success('İş reddedildi');
        setRejectModalOpen(false);
        setAssignedDetailModalOpen(false);
        setRejectReason('');
        loadAssignedJobs();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İş reddedilemedi');
      }
    } catch (error) {
      console.error('İş reddetme hatası:', error);
      toast.error('İş reddedilemedi');
    }
    setRejectLoading(false);
  };

  // Stats
  const stats = useMemo(() => {
    const activeCount = jobs.length;
    const pendingCount = pendingJobs.length;
    const correctionCount = correctionJobs.length;
    const cancelledCount = cancelledJobs.length;
    const totalJobs = activeCount + pendingCount + correctionCount + cancelledCount;
    
    return { activeCount, pendingCount, correctionCount, cancelledCount, totalJobs };
  }, [jobs, pendingJobs, correctionJobs, cancelledJobs]);

  // Unread counts per category
  const unreadCounts = useMemo(() => {
    if (!user?.id) return { pending: 0, active: 0, correction: 0, completed: 0, finalized: 0, forwarded: 0, approved: 0 };
    
    const countUnread = (jobList) => jobList.filter(job => {
      const readBy = job.read_by || [];
      return !readBy.includes(user.id);
    }).length;
    
    return {
      pending: countUnread(pendingJobs),
      active: countUnread(jobs),
      correction: countUnread(correctionJobs),
      completed: countUnread(completedJobs),
      finalized: countUnread(finalizedJobs),
      forwarded: countUnread(forwardedToManagementJobs),
      approved: countUnread(approvedByManagementJobs)
    };
  }, [user?.id, pendingJobs, jobs, correctionJobs, completedJobs, finalizedJobs, forwardedToManagementJobs, approvedByManagementJobs]);

  // Filter jobs based on sub tab
  const getFilteredJobs = () => {
    let filtered = [];
    
    switch (jobSubTab) {
      case 'all':
        filtered = [...jobs, ...pendingJobs, ...correctionJobs];
        break;
      case 'active':
        filtered = jobs;
        break;
      case 'pending':
        filtered = pendingJobs;
        break;
      case 'correction':
        filtered = correctionJobs;
        break;
      case 'completed':
        filtered = completedJobs;
        break;
      case 'finalized':
        filtered = finalizedJobs;
        break;
      case 'cancelled':
        filtered = cancelledJobs;
        break;
      case 'forwarded_to_management':
        filtered = forwardedToManagementJobs;
        break;
      case 'approved_by_management':
        filtered = approvedByManagementJobs;
        break;
      default:
        filtered = [...jobs, ...pendingJobs, ...correctionJobs];
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(job => 
        job.konu?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.aciklama?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sort
    return filtered.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  };

  // Filter talepler based on sub tab - BranchJobs.js yapısına uygun
  const getFilteredTalepler = () => {
    let filtered = [];
    
    switch (taleplerSubTab) {
      case 'gelen':
        // Satın Alma'ya gelen talepler
        filtered = incomingRequests.filter(r => 
          r.status === 'forwarded_to_purchasing' || r.status === 'pending_purchasing' || r.status === 'pending'
        );
        break;
      case 'ise_cevrilen':
        // İşe çevrilen talepler
        filtered = incomingRequests.filter(r => r.status === 'converted_to_job');
        break;
      case 'onayladigim':
        // Onayladığım talepler (converted_to_job hariç)
        filtered = incomingRequests.filter(r => r.status === 'approved' || r.status?.startsWith('forwarded_to_'));
        break;
      case 'bitirilen':
        // Bitirilen talepler (gelen) - sadece completed
        filtered = incomingRequests.filter(r => r.status === 'completed');
        break;
      case 'reddettigim':
        // Reddettiğim talepler
        filtered = incomingRequests.filter(r => r.status === 'rejected_by_purchasing');
        break;
      case 'aktif':
        // Aktif taleplerim (kendi)
        filtered = myRequests.filter(r => !['approved', 'completed', 'converted_to_job', 'rejected', 'rejected_by_branch', 'rejected_by_hr', 'rejected_by_accounting', 'rejected_by_purchasing'].includes(r.status));
        break;
      case 'onaylanan':
        // Onaylanan taleplerim (kendi) - converted_to_job dahil
        filtered = myRequests.filter(r => r.status === 'approved' || r.status === 'converted_to_job');
        break;
      case 'benim_bitirilen':
        // Bitirilen taleplerim (kendi) - sadece completed
        filtered = myRequests.filter(r => r.status === 'completed');
        break;
      case 'reddedilen':
        // Reddedilen taleplerim (kendi)
        filtered = myRequests.filter(r => ['rejected', 'rejected_by_branch', 'rejected_by_hr', 'rejected_by_accounting', 'rejected_by_purchasing'].includes(r.status));
        break;
      default:
        filtered = incomingRequests;
    }
    
    // Apply sort
    return filtered.sort((a, b) => {
      const aVal = a[talepSortConfig.key];
      const bVal = b[talepSortConfig.key];
      
      if (talepSortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  };

  // Handle talep sort
  const handleTalepSort = (key) => {
    setTalepSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort icon for talep
  const getTalepSortIcon = (key) => {
    if (talepSortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return talepSortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-orange-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-orange-600" />;
  };

  // Handle talep action (approve/reject)
  const handleTalepAction = async (action) => {
    if (!selectedTalep) return;
    
    setTalepActionLoading(true);
    try {
      const response = await fetch(`${API}/work/requests/${selectedTalep.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: action === 'approve' ? 'approved' : 'rejected_by_purchasing',
          response_note: talepActionNote,
          handled_by: 'purchasing'
        })
      });
      
      if (response.ok) {
        toast.success(action === 'approve' ? 'Talep onaylandı' : 'Talep reddedildi');
        setSelectedTalep(null);
        setTalepActionNote('');
        loadIncomingRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Talep işlem hatası:', error);
      toast.error('İşlem başarısız');
    }
    setTalepActionLoading(false);
  };

  // Onayla ve İşe Çevir
  const handleApproveAndConvertToJob = () => {
    if (!selectedTalep) return;
    
    // Talebin bilgilerini form'a doldur - şube bilgisini farklı alanlardan al
    const branchId = selectedTalep.branch_id || selectedTalep.owner_branch_id || selectedTalep.sube_id || '';
    
    setFormData({
      konu: selectedTalep.title || '',
      aciklama: selectedTalep.description || '',
      belgeler: selectedTalep.documents || selectedTalep.files || [],
      sube_id: branchId
    });
    
    // İşe çevir modalını aç
    setConvertToJobModalOpen(true);
  };

  // Onayla ve İşe Çevir Submit
  const handleSubmitConvertToJob = async () => {
    if (!formData.konu.trim()) {
      toast.error('İşin konusu zorunludur');
      return;
    }
    if (!selectedTalep) return;
    
    // Şube bilgisini farklı alanlardan al
    const branchId = formData.sube_id || selectedTalep.branch_id || selectedTalep.owner_branch_id || selectedTalep.sube_id || null;
    const branchName = selectedTalep.branch_name || selectedTalep.owner_branch_name || branches.find(b => b.id === branchId)?.name || '';
    
    setSubmitting(true);
    try {
      // 1. İşi oluştur (source_request bilgisi ile)
      const jobResponse = await fetch(`${API}/purchasing/jobs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          konu: formData.konu,
          aciklama: formData.aciklama,
          belgeler: formData.belgeler,
          sube_id: branchId,
          branch_name: branchName,
          source_request_id: selectedTalep.id,
          source_request: {
            id: selectedTalep.id,
            request_number: selectedTalep.request_number,
            title: selectedTalep.title,
            category: selectedTalep.category,
            owner_name: selectedTalep.owner_name,
            branch_id: branchId,
            branch_name: branchName
          }
        })
      });
      
      if (!jobResponse.ok) {
        const error = await jobResponse.json();
        toast.error(error.detail || 'İş oluşturulamadı');
        setSubmitting(false);
        return;
      }
      
      // 2. Talebi "converted_to_job" olarak işaretle
      await fetch(`${API}/work/requests/${selectedTalep.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          status: 'converted_to_job',
          response_note: 'İşe dönüştürüldü'
        })
      });
      
      toast.success('Talep onaylandı ve iş olarak eklendi');
      setConvertToJobModalOpen(false);
      setSelectedTalep(null);
      setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
      loadIncomingRequests();
      loadPendingJobs();
      setActiveTab('active');
      setJobSubTab('pending');
    } catch (error) {
      console.error('İşe dönüştürme hatası:', error);
      toast.error('İşe dönüştürme başarısız');
    }
    setSubmitting(false);
  };

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-orange-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-orange-600" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-orange-600" />
            Satın Alma İşleri
          </h1>
          <p className="text-gray-500">Satın alma departmanı iş yönetimi</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Aktif İş</p>
                <p className="text-2xl font-bold">{stats.activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Onay Bekleyen</p>
                <p className="text-2xl font-bold">{stats.pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Düzeltilmeli</p>
                <p className="text-2xl font-bold">{stats.correctionCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Ban className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">İptal Edilen</p>
                <p className="text-2xl font-bold">{stats.cancelledCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="İş ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-jobs-input"
          />
        </div>
      </div>

      {/* Tabs - 2 Ana Sekme */}
      <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="active">
        <TabsList className="grid w-full grid-cols-2 max-w-xl">
          <TabsTrigger value="active" className="gap-2" data-testid="tab-active">
            <Briefcase className="h-4 w-4" />
            AKTİF İŞLER
          </TabsTrigger>
          {/* TALEPLER TAB - ŞİMDİLİK KALDIRILDI */}
          <TabsTrigger value="archive" className="gap-2" data-testid="tab-archive">
            <Archive className="h-4 w-4" />
            ARŞİV
          </TabsTrigger>
        </TabsList>

        {/* AKTİF İŞLER Tab */}
        <TabsContent value="active" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-orange-600" />
                Aktif İşler
              </CardTitle>
              <div className="flex gap-2">
                <Button onClick={() => setAddModalOpen(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="add-job-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  İş Ekle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Alt Durum Filtreleri */}
              <div className="flex gap-2 mb-4 border-b flex-wrap">
                <button
                  onClick={() => setJobSubTab('pending')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'pending' 
                      ? 'border-yellow-600 text-yellow-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Onay Bekleyen
                  {unreadCounts.pending > 0 ? (
                    <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                      {unreadCounts.pending} Yeni
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-700">
                      {pendingJobs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setJobSubTab('active')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'active' 
                      ? 'border-emerald-600 text-emerald-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Devam Eden
                  {unreadCounts.active > 0 ? (
                    <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                      {unreadCounts.active} Yeni
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">
                      {jobs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setJobSubTab('forwarded_to_management')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'forwarded_to_management' 
                      ? 'border-purple-600 text-purple-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Yönetime İletilen
                  {unreadCounts.forwarded > 0 ? (
                    <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                      {unreadCounts.forwarded} Yeni
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-700">
                      {forwardedToManagementJobs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setJobSubTab('approved_by_management')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'approved_by_management' 
                      ? 'border-indigo-600 text-indigo-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Yönetim Onayladı
                  {unreadCounts.approved > 0 ? (
                    <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                      {unreadCounts.approved} Yeni
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2 bg-indigo-100 text-indigo-700">
                      {approvedByManagementJobs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setJobSubTab('completed')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'completed' 
                      ? 'border-blue-600 text-blue-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Hazırlanan İşler
                  {unreadCounts.completed > 0 ? (
                    <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                      {unreadCounts.completed} Yeni
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">
                      {completedJobs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setJobSubTab('finalized')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'finalized' 
                      ? 'border-green-600 text-green-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Tamamlanan
                  {unreadCounts.finalized > 0 ? (
                    <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                      {unreadCounts.finalized} Yeni
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                      {finalizedJobs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setJobSubTab('correction')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'correction' 
                      ? 'border-orange-600 text-orange-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Düzeltme
                  {unreadCounts.correction > 0 ? (
                    <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                      {unreadCounts.correction} Yeni
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-700">
                      {correctionJobs.length}
                    </Badge>
                  )}
                </button>
                <button
                  onClick={() => setJobSubTab('cancelled')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    jobSubTab === 'cancelled' 
                      ? 'border-red-600 text-red-600' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="tab-cancelled"
                >
                  İptal Edilen
                  <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">
                    {cancelledJobs.length}
                  </Badge>
                </button>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
                </div>
              ) : getFilteredJobs().length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Bu kategoride iş bulunamadı</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-orange-50">
                        <TableHead className="cursor-pointer hover:bg-orange-100" onClick={() => handleSort('konu')}>
                          <div className="flex items-center gap-1">İş Adı <SortIcon columnKey="konu" /></div>
                        </TableHead>
                        <TableHead>İş Türü</TableHead>
                        <TableHead>Şube</TableHead>
                        <TableHead>Şirket</TableHead>
                        <TableHead>Takip Edenler</TableHead>
                        <TableHead className="cursor-pointer hover:bg-orange-100" onClick={() => handleSort('created_at')}>
                          <div className="flex items-center gap-1">Başlangıç <SortIcon columnKey="created_at" /></div>
                        </TableHead>
                        <TableHead>Tahmini Bitiş</TableHead>
                        <TableHead>Konum(lar)</TableHead>
                        <TableHead>İş İlerleme</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Son Güncelleme</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredJobs().map((job) => {
                        const statusConfig = getStatusConfig(job.status);
                        const StatusIcon = statusConfig.icon;
                        const jobIsUnread = isUnread(job);
                        const branchJob = job.source_branch_job || {};
                        const progress = branchJob.progress || 0;
                        const isCancelled = job.status === 'cancelled';
                        return (
                          <TableRow 
                            key={job.id} 
                            className={`hover:bg-orange-50/50 cursor-pointer ${isCancelled ? 'bg-red-50' : ''} ${jobIsUnread ? 'bg-blue-50/50 border-l-4 border-l-red-500' : ''}`}
                            onClick={() => {
                              if (jobIsUnread) {
                                markAsRead(job.id);
                              }
                              setSelectedJob(job);
                              setDetailModalOpen(true);
                            }}
                          >
                            {/* İş Adı */}
                            <TableCell className="font-medium max-w-[200px]">
                              <div className="flex items-center gap-2">
                                {jobIsUnread && (
                                  <Badge className="h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold animate-pulse">
                                    YENİ
                                  </Badge>
                                )}
                                <span className="line-clamp-1">{job.amac || job.konu || branchJob.name || '-'}</span>
                                {/* Belge göstergesi - duplicate olmadan */}
                                {((job.belgeler && job.belgeler.length > 0) || 
                                  (job.documents && job.documents.length > 0)) && (
                                  <Badge className="h-5 px-1.5 bg-blue-100 text-blue-700 text-[10px]" title="Ekli belge var">
                                    <File className="h-3 w-3 mr-0.5" />
                                    {(job.belgeler?.length || 0) + (job.documents?.length || 0)}
                                  </Badge>
                                )}
                              </div>
                              {job.forwarded_from === 'branch' && (
                                <Badge className="mt-1 bg-indigo-100 text-indigo-700 text-[10px]">
                                  Şubeden İletildi
                                </Badge>
                              )}
                            </TableCell>
                            
                            {/* İş Türü */}
                            <TableCell>
                              {job.job_type === 'urun_satin_alma' ? (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                  <Package className="h-3 w-3 mr-1" />
                                  Ürün
                                </Badge>
                              ) : job.job_type === 'hizmet_satin_alma' ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <Briefcase className="h-3 w-3 mr-1" />
                                  Hizmet
                                </Badge>
                              ) : job.job_type === 'diger' ? (
                                <Badge className="bg-gray-100 text-gray-700 border-gray-200">
                                  <FileText className="h-3 w-3 mr-1" />
                                  Diğer
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-400">
                                  Belirtilmemiş
                                </Badge>
                              )}
                            </TableCell>
                            
                            {/* Şube */}
                            <TableCell className="text-sm">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                {job.branch_name || branches.find(b => b.id === job.sube_id)?.name || job.source_request?.branch_name || branchJob.branch_name || '-'}
                              </Badge>
                            </TableCell>
                            
                            {/* Şirket */}
                            <TableCell className="text-sm">
                              {branchJob.company || job.talep_eden || '-'}
                            </TableCell>
                            
                            {/* Takip Edenler */}
                            <TableCell>
                              <span className="text-sm text-gray-600">
                                {job.created_by_name || '-'}
                              </span>
                            </TableCell>
                            
                            {/* Başlangıç */}
                            <TableCell className="text-sm text-gray-500">
                              {branchJob.start_date || new Date(job.created_at).toLocaleDateString('tr-TR')}
                            </TableCell>
                            
                            {/* Tahmini Bitiş */}
                            <TableCell className="text-sm text-gray-500">
                              {branchJob.estimated_end_date || '-'}
                            </TableCell>
                            
                            {/* Konum(lar) */}
                            <TableCell>
                              {branchJob.locations && branchJob.locations.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {branchJob.locations.slice(0, 2).map((loc, idx) => (
                                    <Badge key={idx} variant="secondary" className="text-xs">
                                      {loc}
                                    </Badge>
                                  ))}
                                  {branchJob.locations.length > 2 && (
                                    <Badge variant="secondary" className="text-xs">
                                      +{branchJob.locations.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                            
                            {/* İş İlerleme */}
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      progress >= 80 ? 'bg-green-500' : 
                                      progress >= 50 ? 'bg-amber-500' : 
                                      progress >= 25 ? 'bg-orange-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium">{progress}%</span>
                              </div>
                            </TableCell>
                            
                            {/* Durum */}
                            <TableCell>
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            
                            {/* Son Güncelleme */}
                            <TableCell className="text-sm text-gray-500">
                              <div>{new Date(job.updated_at || job.created_at).toLocaleDateString('tr-TR')}</div>
                              <div className="text-xs text-gray-400">
                                {new Date(job.updated_at || job.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </TableCell>
                            
                            {/* İşlemler */}
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                {/* Sadece Detay butonu - Onay/Reddet işlemleri detay modalından yapılacak */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { 
                                    if (jobIsUnread) markAsRead(job.id);
                                    setSelectedJob(job); 
                                    setDetailModalOpen(true); 
                                  }}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Detay ve İşlemler"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Talepler Tab */}
        <TabsContent value="archive" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Archive className="h-5 w-5 text-gray-600" />
                Arşiv
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
                </div>
              ) : archivedJobs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Archive className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Arşivlenmiş iş yok</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Konu</TableHead>
                        <TableHead>İş Türü</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Şube</TableHead>
                        <TableHead className="text-right">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedJobs.map((job) => (
                        <TableRow key={job.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium max-w-[300px]">
                            <span className="line-clamp-1">{job.konu}</span>
                          </TableCell>
                          <TableCell>
                            {job.job_type === 'urun_satin_alma' ? (
                              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                <Package className="h-3 w-3 mr-1" />
                                Ürün
                              </Badge>
                            ) : job.job_type === 'hizmet_satin_alma' ? (
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <Briefcase className="h-3 w-3 mr-1" />
                                Hizmet
                              </Badge>
                            ) : job.job_type === 'diger' ? (
                              <Badge className="bg-gray-100 text-gray-700 border-gray-200">
                                <FileText className="h-3 w-3 mr-1" />
                                Diğer
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-400">
                                Belirtilmemiş
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(job.created_at).toLocaleDateString('tr-TR')}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-gray-100 text-gray-700">
                              <Archive className="h-3 w-3 mr-1" />
                              Arşivlendi
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">
                              {job.branch_name || branches.find(b => b.id === job.sube_id)?.name || job.source_request?.branch_name || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedJob(job); setDetailModalOpen(true); }}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Talep Detay Modal */}
      <Dialog open={!!selectedTalep} onOpenChange={() => { setSelectedTalep(null); setTalepActionNote(''); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Talep Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedTalep && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm">Talep No</Label>
                  <p className="font-mono text-indigo-700">{selectedTalep.request_number || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Tarih</Label>
                  <p>
                    {new Date(selectedTalep.created_at).toLocaleDateString('tr-TR')}
                    <span className="text-gray-500 ml-2">
                      {new Date(selectedTalep.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Başlık</Label>
                <p className="font-medium">{selectedTalep.title}</p>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Açıklama</Label>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedTalep.description || '-'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm">Kategori</Label>
                  <p>{selectedTalep.category}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Öncelik</Label>
                  <Badge className={
                    selectedTalep.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    selectedTalep.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                    selectedTalep.priority === 'normal' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }>
                    {selectedTalep.priority === 'urgent' ? 'Acil' :
                     selectedTalep.priority === 'high' ? 'Yüksek' :
                     selectedTalep.priority === 'normal' ? 'Normal' : 'Düşük'}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Gönderen</Label>
                <p className="font-medium">{selectedTalep.owner_name || 'Bilinmiyor'}</p>
              </div>

              {/* Belgeler - tüm olası alanları kontrol et */}
              {(() => {
                const allFiles = [
                  ...(selectedTalep.files || []),
                  ...(selectedTalep.documents || []),
                  ...(selectedTalep.belgeler || []),
                  ...(selectedTalep.attachments || []),
                  ...(selectedTalep.form_data?.files || []),
                  ...(selectedTalep.form_data?.documents || []),
                  ...(selectedTalep.form_data?.belgeler || []),
                  ...(selectedTalep.source_branch_job?.documents || []),
                  ...(selectedTalep.source_branch_job?.belgeler || [])
                ];
                return allFiles.length > 0 ? (
                  <div>
                    <Label className="text-gray-500 text-sm">Belgeler ({allFiles.length})</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {allFiles.map((file, index) => (
                        <a
                          key={index}
                          href={file.url || file.data || file.base64}
                          download={file.name || file.filename || `belge-${index + 1}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm hover:bg-indigo-100 transition-colors"
                        >
                          <Download className="h-4 w-4 text-indigo-600" />
                          <span className="text-indigo-700">{file.name || file.filename || `Belge ${index + 1}`}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* İşlem Butonları */}
              {(selectedTalep.status === 'pending' || selectedTalep.status === 'pending_purchasing' || selectedTalep.status === 'forwarded_to_purchasing') && (
                <div className="border-t pt-4 mt-4 space-y-4">
                  <Label className="text-gray-700 font-medium">İşlem Yap</Label>
                  
                  <div>
                    <Label className="text-gray-500 text-sm">Açıklama / Not</Label>
                    <Textarea
                      value={talepActionNote}
                      onChange={(e) => setTalepActionNote(e.target.value)}
                      placeholder="Onay veya red açıklaması..."
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleTalepAction('approve')}
                      disabled={talepActionLoading}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="talep-approve-btn"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Onayla
                    </Button>
                    <Button
                      onClick={handleApproveAndConvertToJob}
                      disabled={talepActionLoading}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="talep-approve-convert-btn"
                    >
                      <Briefcase className="h-4 w-4 mr-2" />
                      Onayla ve İşe Çevir
                    </Button>
                    <Button
                      onClick={() => handleTalepAction('reject')}
                      disabled={talepActionLoading}
                      variant="destructive"
                      data-testid="talep-reject-btn"
                    >
                      {talepActionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                      Reddet
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Talep Hareketi */}
              <RequestHistory request={selectedTalep} />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelectedTalep(null); setTalepActionNote(''); }}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onayla ve İşe Çevir Modal */}
      <Dialog open={convertToJobModalOpen} onOpenChange={(open) => {
        setConvertToJobModalOpen(open);
        if (!open) {
          setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <Briefcase className="h-5 w-5" />
              Talebi Onayla ve İşe Çevir
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Kaynak Talep Bilgisi */}
            {selectedTalep && (
              <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-2 text-sm text-indigo-800">
                  <Inbox className="h-4 w-4" />
                  <span className="font-medium">Kaynak Talep:</span>
                  <span>{selectedTalep.request_number}</span>
                  <span className="text-indigo-600">- {selectedTalep.owner_name}</span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="convert-konu">İşin Konusu *</Label>
              <Input
                id="convert-konu"
                value={formData.konu}
                onChange={(e) => setFormData(prev => ({ ...prev, konu: e.target.value }))}
                placeholder="İşin konusu"
                data-testid="convert-job-konu"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="convert-aciklama">Açıklama</Label>
              <Textarea
                id="convert-aciklama"
                value={formData.aciklama}
                onChange={(e) => setFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="İşin açıklaması"
                rows={3}
                data-testid="convert-job-aciklama"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="convert-sube">Şube</Label>
              <Select
                value={formData.sube_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, sube_id: value }))}
              >
                <SelectTrigger data-testid="convert-job-sube">
                  <SelectValue placeholder="Şube seçin (opsiyonel)" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Seçilmezse kullanıcının şubesi kullanılır</p>
            </div>
            
            {/* Belge Yükleme */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Upload className="h-4 w-4" />
                Belgeler
              </Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    files.forEach(file => {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        setFormData(prev => ({
                          ...prev,
                          belgeler: [...prev.belgeler, {
                            name: file.name,
                            type: file.type,
                            size: file.size,
                            data: event.target.result
                          }]
                        }));
                      };
                      reader.readAsDataURL(file);
                    });
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="convert-job-file-upload-purchasing"
                  data-testid="convert-job-file-input"
                />
                <label htmlFor="convert-job-file-upload-purchasing" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">Belge yüklemek için tıklayın</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim dosyaları</p>
                </label>
              </div>
              
              {/* Yüklenen Belgeler Listesi */}
              {formData.belgeler && formData.belgeler.length > 0 && (
                <div className="space-y-1 mt-2">
                  <p className="text-xs text-gray-500 font-medium">Ekli Belgeler ({formData.belgeler.length})</p>
                  {formData.belgeler.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-500" />
                        <span className="truncate max-w-[250px]">{doc.name || doc.filename || `Belge ${idx + 1}`}</span>
                        {doc.size && (
                          <span className="text-xs text-gray-400">({(doc.size / 1024).toFixed(1)} KB)</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            belgeler: prev.belgeler.filter((_, i) => i !== idx)
                          }));
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setConvertToJobModalOpen(false);
                setFormData({ konu: '', aciklama: '', belgeler: [], sube_id: '' });
              }}
              data-testid="convert-job-cancel"
            >
              İptal
            </Button>
            <Button 
              onClick={handleSubmitConvertToJob}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="convert-job-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Briefcase className="h-4 w-4 mr-2" />
                  Onayla ve İşe Çevir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Detay Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-orange-600" />
              İş Detayı
            </DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-500 text-sm">Konu</Label>
                <p className="font-medium">{selectedJob.konu}</p>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Açıklama</Label>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.aciklama || '-'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-sm">Durum</Label>
                  <Badge className={getStatusConfig(selectedJob.status).color}>
                    {getStatusConfig(selectedJob.status).label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500 text-sm">Tarih</Label>
                  <p>{new Date(selectedJob.created_at).toLocaleString('tr-TR')}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500 text-sm">Şube</Label>
                <p>{selectedJob.branch_name || branches.find(b => b.id === selectedJob.sube_id)?.name || selectedJob.source_request?.branch_name || '-'}</p>
              </div>

              {/* İletme Bilgisi - Şubeden iletilmiş işler için göster */}
              {selectedJob.forwarded_from && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <Label className="text-indigo-700 text-sm font-semibold flex items-center gap-2 mb-2">
                    <Send className="h-4 w-4" />
                    İletme Bilgisi
                  </Label>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-gray-500">Kaynak:</span>{' '}
                      <Badge className="bg-indigo-100 text-indigo-700">
                        {selectedJob.forwarded_from === 'branch' ? 'Şube Yönetimi' : 
                         selectedJob.forwarded_from === 'work' ? 'İş Paneli' :
                         selectedJob.forwarded_from === 'hr' ? 'İK' : selectedJob.forwarded_from}
                      </Badge>
                    </p>
                    {selectedJob.forwarded_by_name && (
                      <p><span className="text-gray-500">İleten:</span> {selectedJob.forwarded_by_name}</p>
                    )}
                    {selectedJob.forwarded_at && (
                      <p><span className="text-gray-500">İletme Tarihi:</span> {new Date(selectedJob.forwarded_at).toLocaleString('tr-TR')}</p>
                    )}
                    {selectedJob.forwarded_note && (
                      <div>
                        <span className="text-gray-500">İletme Notu:</span>
                        <p className="mt-1 text-indigo-700 bg-white p-2 rounded border border-indigo-200">{selectedJob.forwarded_note}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Belgeler - tüm olası alanları kontrol et */}
              {(() => {
                // Duplicate'ları önlemek için sadece documents kullan (source_branch_job.documents aynı)
                const allJobFiles = [
                  ...(selectedJob.belgeler || []),
                  ...(selectedJob.documents || []),
                  ...(selectedJob.files || [])
                ];
                
                // Benzersiz belgeleri filtrele (id veya stored_filename'e göre)
                const uniqueFiles = allJobFiles.filter((file, index, self) => 
                  index === self.findIndex(f => 
                    (f.id && f.id === file.id) || 
                    (f.stored_filename && f.stored_filename === file.stored_filename) ||
                    (f.name && f.name === file.name && !f.id && !f.stored_filename)
                  )
                );
                
                // Belge URL'sini oluştur - public endpoint kullan (auth gerektirmez)
                const getPreviewUrl = (file) => {
                  if (file.id) {
                    return `${process.env.REACT_APP_BACKEND_URL}/api/public/documents/${file.id}/view`;
                  }
                  return file.url || file.data || file.base64 || '#';
                };
                
                const getDownloadUrl = (file) => {
                  if (file.id) {
                    return `${process.env.REACT_APP_BACKEND_URL}/api/public/documents/${file.id}/download`;
                  }
                  return file.url || file.data || file.base64 || '#';
                };
                
                return uniqueFiles.length > 0 ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Label className="text-blue-700 text-sm font-semibold flex items-center gap-2">
                      <File className="h-4 w-4" />
                      Ekli Belgeler ({uniqueFiles.length})
                    </Label>
                    <div className="flex flex-col gap-2 mt-2">
                      {uniqueFiles.map((file, index) => (
                        <div
                          key={file.id || file.stored_filename || index}
                          className="flex items-center justify-between p-2 bg-white rounded border border-blue-100"
                        >
                          <div className="flex items-center gap-2">
                            <File className="h-4 w-4 text-blue-500" />
                            <span className="text-sm font-medium">{file.original_filename || file.name || file.filename || `Belge ${index + 1}`}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => window.open(getPreviewUrl(file), '_blank')}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Görüntüle
                            </Button>
                            <a href={getDownloadUrl(file)} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700">
                                <Download className="h-3 w-3 mr-1" />
                                İndir
                              </Button>
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* SEÇİLEN TEKLİF - Yönetim onayladıysa göster */}
              {selectedJob.selected_teklif && (
                <div className="bg-green-100 border-2 border-green-500 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <Label className="text-green-700 text-xl font-bold">SEÇİLEN TEKLİF</Label>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-gray-500 text-sm">Firma Adı:</span>
                        <p className="font-bold text-gray-800 text-lg">{selectedJob.selected_teklif.firma_adi || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Toplam Fiyat:</span>
                        <p className="font-bold text-green-700 text-2xl">{selectedJob.selected_teklif.toplam_fiyat || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Ödeme Planı:</span>
                        <p className="font-semibold text-gray-800">{selectedJob.selected_teklif.odeme_plani || '-'}</p>
                      </div>
                    </div>
                    
                    {/* Teklif Açıklaması */}
                    {selectedJob.selected_teklif.aciklama && (
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <span className="text-gray-500 text-sm">Teklif Açıklaması:</span>
                        <p className="mt-1 text-gray-700 bg-green-50 p-3 rounded-md whitespace-pre-wrap">{selectedJob.selected_teklif.aciklama}</p>
                      </div>
                    )}
                    
                    {selectedJob.selected_teklif.belgeler && selectedJob.selected_teklif.belgeler.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-gray-500 text-sm">Belgeler:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedJob.selected_teklif.belgeler.map((file, idx) => (
                            <a
                              key={idx}
                              href={file.url || file.data}
                              download={file.name}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded text-sm hover:bg-green-200"
                            >
                              <Download className="h-3 w-3 text-green-600" />
                              {file.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {selectedJob.approval_note && (
                    <div className="mt-3 pt-3 border-t border-green-300">
                      <span className="text-green-700 text-sm font-medium">Yönetim Notu:</span>
                      <p className="text-gray-800 mt-1">{selectedJob.approval_note}</p>
                    </div>
                  )}
                  
                  <div className="mt-3 pt-3 border-t border-green-300 text-sm text-green-700">
                    <span>Onaylayan: </span>
                    <span className="font-semibold">{selectedJob.approved_by_management_user_name || '-'}</span>
                    {selectedJob.approved_by_management_at && (
                      <span className="ml-2">({new Date(selectedJob.approved_by_management_at).toLocaleString('tr-TR')})</span>
                    )}
                  </div>
                </div>
              )}

              {/* Hazırlanan Talep Detayları - Yönetime iletilmişse göster */}
              {selectedJob.prepared_request && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-purple-600" />
                    <Label className="text-lg font-semibold text-purple-700">Hazırlanan Talep</Label>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500 text-sm">Tarih</Label>
                        <p className="font-medium">{selectedJob.prepared_request.tarih || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 text-sm">Saat</Label>
                        <p className="font-medium">{selectedJob.prepared_request.saat || '-'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-gray-500 text-sm">İşin Adı</Label>
                      <p className="font-medium">{selectedJob.prepared_request.is_adi || '-'}</p>
                    </div>
                    
                    {selectedJob.prepared_request.aciklama && (
                      <div>
                        <Label className="text-gray-500 text-sm">Açıklama</Label>
                        <p className="text-gray-700 whitespace-pre-wrap">{selectedJob.prepared_request.aciklama}</p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-gray-500 text-sm">Hazırlayan</Label>
                      <p className="font-medium">{selectedJob.prepared_request.prepared_by_name || '-'}</p>
                      <p className="text-xs text-gray-500">
                        {selectedJob.prepared_request.prepared_at && new Date(selectedJob.prepared_request.prepared_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                    
                    {/* Teklifler */}
                    {selectedJob.prepared_request.teklifler && selectedJob.prepared_request.teklifler.length > 0 && (
                      <div className="mt-4">
                        <Label className="text-gray-700 font-semibold mb-3 block">
                          Teklifler ({selectedJob.prepared_request.teklifler.length})
                        </Label>
                        <div className="space-y-3">
                          {selectedJob.prepared_request.teklifler.map((teklif, index) => (
                            <div key={index} className="bg-white border border-purple-200 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-purple-700">{teklif.sira || index + 1}. Teklif</span>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-500">Firma:</span>
                                  <p className="font-medium">{teklif.firma_adi || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Toplam Fiyat:</span>
                                  <p className="font-medium text-green-700">{teklif.toplam_fiyat || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500">Ödeme Planı:</span>
                                  <p className="font-medium">{teklif.odeme_plani || '-'}</p>
                                </div>
                              </div>
                              {/* Teklif Açıklaması */}
                              {teklif.aciklama && (
                                <div className="mt-2 pt-2 border-t border-purple-100">
                                  <span className="text-gray-500 text-sm">Açıklama:</span>
                                  <p className="text-gray-700 mt-1 text-sm whitespace-pre-wrap">{teklif.aciklama}</p>
                                </div>
                              )}
                              {/* Teklif Belgeleri */}
                              {teklif.belgeler && teklif.belgeler.length > 0 && (
                                <div className="mt-2 pt-2 border-t">
                                  <span className="text-gray-500 text-sm">Belgeler:</span>
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {teklif.belgeler.map((file, fileIndex) => (
                                      <a
                                        key={fileIndex}
                                        href={file.url || file.data}
                                        download={file.name}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded text-xs hover:bg-purple-200"
                                      >
                                        <Download className="h-3 w-3" />
                                        {file.name}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {/* Teklifleri Karşılaştır Butonu */}
                        {selectedJob.prepared_request.teklifler.filter(t => t.offer_id).length >= 2 && (
                          <div className="mt-4 flex justify-center">
                            <Button
                              variant="default"
                              className="bg-indigo-600 hover:bg-indigo-700 gap-2"
                              onClick={() => openJobCompare(selectedJob.prepared_request.teklifler)}
                              data-testid="compare-job-offers-btn"
                            >
                              <GitCompareArrows className="h-4 w-4" />
                              Teklifleri Karşılaştır ({selectedJob.prepared_request.teklifler.filter(t => t.offer_id).length})
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* OLUŞTURULAN SİPARİŞ - Tamamlanan işlerde göster */}
              {selectedJob.status === 'completed' && selectedJob.final_order_details && (
                <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ShoppingCart className="h-6 w-6 text-blue-600" />
                    <Label className="text-blue-700 text-xl font-bold">OLUŞTURULAN SİPARİŞ</Label>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-blue-300 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-500 text-sm">Tedarikçi (Cari Hesap):</span>
                        <p className="font-bold text-gray-800 text-lg">{selectedJob.final_order_details.cari_hesap_name || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Sipariş Tarihi:</span>
                        <p className="font-semibold text-gray-800">
                          {selectedJob.final_order_details.entry_datetime 
                            ? new Date(selectedJob.final_order_details.entry_datetime).toLocaleString('tr-TR')
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-sm">Tahmini Geliş:</span>
                        <p className="font-semibold text-gray-800">
                          {selectedJob.final_order_details.estimated_arrival_datetime 
                            ? new Date(selectedJob.final_order_details.estimated_arrival_datetime).toLocaleString('tr-TR')
                            : '-'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Sipariş Kalemleri */}
                    {selectedJob.final_order_details.items && selectedJob.final_order_details.items.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-gray-500 text-sm font-medium">Sipariş Kalemleri:</span>
                        <div className="mt-2 border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-blue-100">
                              <tr>
                                <th className="px-3 py-2 text-left">Ürün</th>
                                <th className="px-3 py-2 text-center">Miktar</th>
                                <th className="px-3 py-2 text-right">Fiyat</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedJob.final_order_details.items.map((item, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="px-3 py-2">{item.product_name || '-'}</td>
                                  <td className="px-3 py-2 text-center">{item.quantity} {item.unit}</td>
                                  <td className="px-3 py-2 text-right text-green-700">
                                    {item.purchase_price ? `${item.purchase_price} ₺` : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Sipariş Belgeleri */}
                    {selectedJob.final_order_details.documents && selectedJob.final_order_details.documents.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-gray-500 text-sm">Sipariş Belgeleri:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedJob.final_order_details.documents.map((file, idx) => (
                            <a
                              key={idx}
                              href={file.data || file.url}
                              download={file.name}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2 py-1 bg-blue-100 rounded text-sm hover:bg-blue-200"
                            >
                              <Download className="h-3 w-3 text-blue-600" />
                              {file.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-blue-300 text-sm text-blue-700">
                    <span>Tamamlayan: </span>
                    <span className="font-semibold">{selectedJob.completed_by_name || '-'}</span>
                    {selectedJob.completed_at && (
                      <span className="ml-2">({new Date(selectedJob.completed_at).toLocaleString('tr-TR')})</span>
                    )}
                  </div>
                </div>
              )}

              {/* TASLAK DETAYLARI - Hazırlanan ve Tamamlanan işlerde göster */}
              {(selectedJob.status === 'completed' || selectedJob.status === 'finalized') && selectedJob.draft_type && selectedJob.draft_data && (
                <div className={`${selectedJob.status === 'finalized' ? 'bg-green-50 border-2 border-green-400' : 'bg-amber-50 border-2 border-amber-400'} rounded-lg p-4 mt-4`}>
                  <div className="flex items-center gap-2 mb-3">
                    {selectedJob.draft_type === 'order' && <ShoppingCart className={`h-6 w-6 ${selectedJob.status === 'finalized' ? 'text-green-600' : 'text-amber-600'}`} />}
                    {selectedJob.draft_type === 'demirbas' && <Package className={`h-6 w-6 ${selectedJob.status === 'finalized' ? 'text-green-600' : 'text-amber-600'}`} />}
                    {selectedJob.draft_type === 'transfer' && <ArrowLeftRight className={`h-6 w-6 ${selectedJob.status === 'finalized' ? 'text-green-600' : 'text-amber-600'}`} />}
                    {selectedJob.draft_type === 'info' && <Info className={`h-6 w-6 ${selectedJob.status === 'finalized' ? 'text-green-600' : 'text-amber-600'}`} />}
                    <Label className={`${selectedJob.status === 'finalized' ? 'text-green-700' : 'text-amber-700'} text-xl font-bold`}>
                      {selectedJob.draft_type === 'order' && (selectedJob.status === 'finalized' ? 'ONAYLANAN SİPARİŞ' : 'SİPARİŞ TASLAĞI')}
                      {selectedJob.draft_type === 'demirbas' && (selectedJob.status === 'finalized' ? 'ONAYLANAN DEMİRBAŞ' : 'DEMİRBAŞ TASLAĞI')}
                      {selectedJob.draft_type === 'transfer' && (selectedJob.status === 'finalized' ? 'ONAYLANAN TRANSFER' : 'TRANSFER TASLAĞI')}
                      {selectedJob.draft_type === 'info' && (selectedJob.status === 'finalized' ? 'ONAYLANAN BİLGİLENDİRME' : 'BİLGİLENDİRME TASLAĞI')}
                    </Label>
                    <Badge className={selectedJob.status === 'finalized' ? 'bg-green-200 text-green-800' : 'bg-amber-200 text-amber-800'}>
                      {selectedJob.status === 'finalized' ? 'Onaylandı' : 'Onay Bekliyor'}
                    </Badge>
                  </div>
                  
                  <div className={`bg-white rounded-lg p-4 border ${selectedJob.status === 'finalized' ? 'border-green-300' : 'border-amber-300'} space-y-3`}>
                    {/* SİPARİŞ TASLAĞI */}
                    {selectedJob.draft_type === 'order' && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-gray-500 text-sm">Hedef Şube:</span>
                            <p className="font-bold text-gray-800">{selectedJob.draft_data.branch_name || selectedJob.draft_data.branch_id || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">Tedarikçi (Cari Hesap):</span>
                            <p className="font-bold text-gray-800">{selectedJob.draft_data.cari_hesap_name || selectedJob.draft_data.supplier || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">Sipariş Tarihi:</span>
                            <p className="font-semibold text-gray-800">
                              {selectedJob.draft_data.entry_datetime 
                                ? new Date(selectedJob.draft_data.entry_datetime).toLocaleString('tr-TR')
                                : '-'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">Tahmini Geliş:</span>
                            <p className="font-semibold text-gray-800">
                              {selectedJob.draft_data.estimated_arrival_datetime 
                                ? new Date(selectedJob.draft_data.estimated_arrival_datetime).toLocaleString('tr-TR')
                                : '-'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Sipariş Kalemleri */}
                        {selectedJob.draft_data.items && selectedJob.draft_data.items.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="text-gray-500 text-sm font-medium">Sipariş Kalemleri:</span>
                            <div className="mt-2 border rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-amber-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Ürün</th>
                                    <th className="px-3 py-2 text-center">Miktar</th>
                                    <th className="px-3 py-2 text-right">Fiyat</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedJob.draft_data.items.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                      <td className="px-3 py-2">{item.product_name || item.name || '-'}</td>
                                      <td className="px-3 py-2 text-center">{item.quantity} {item.unit || 'adet'}</td>
                                      <td className="px-3 py-2 text-right text-green-700">
                                        {item.purchase_price || item.price ? `${item.purchase_price || item.price} ₺` : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {selectedJob.draft_data.description && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="text-gray-500 text-sm">Açıklama:</span>
                            <p className="text-gray-800">{selectedJob.draft_data.description}</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* DEMİRBAŞ TASLAĞI */}
                    {selectedJob.draft_type === 'demirbas' && (
                      <>
                        {(() => {
                          const dd = selectedJob.draft_data || {};
                          const products = (dd.products && dd.products.length > 0)
                            ? dd.products
                            : [{ product_name: dd.product_name, brand: dd.brand, serial_number: dd.serial_number, quantity: dd.quantity, unit: dd.unit, purchase_price: dd.purchase_price }];
                          return (
                            <>
                              <div className="space-y-3">
                                {products.map((p, idx) => (
                                  <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                                    {products.length > 1 && (
                                      <div className="text-xs font-semibold text-amber-700 mb-2">Ürün {idx + 1}</div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div>
                                        <span className="text-gray-500 text-sm">Ürün Adı:</span>
                                        <p className="font-bold text-gray-800">{p.product_name || '-'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-sm">Marka:</span>
                                        <p className="font-semibold text-gray-800">{p.brand || '-'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-sm">Seri No:</span>
                                        <p className="font-semibold text-gray-800">{p.serial_number || '-'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-sm">Miktar:</span>
                                        <p className="font-semibold text-gray-800">{p.quantity || 1} {p.unit || 'adet'}</p>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 text-sm">Alış Fiyatı:</span>
                                        <p className="font-semibold text-green-700">{p.purchase_price ? `${p.purchase_price} ₺` : '-'}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                <div>
                                  <span className="text-gray-500 text-sm">Tedarikçi:</span>
                                  <p className="font-semibold text-gray-800">{dd.supplier || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500 text-sm">Konum:</span>
                                  <p className="font-semibold text-gray-800">{dd.location || '-'}</p>
                                </div>
                                <div>
                                  <span className="text-gray-500 text-sm">Kullanım Durumu:</span>
                                  <p className="font-semibold text-gray-800">{dd.usage_status || '-'}</p>
                                </div>
                              </div>

                              {dd.description && (
                                <div className="mt-3 pt-3 border-t">
                                  <span className="text-gray-500 text-sm">Açıklama:</span>
                                  <p className="text-gray-800">{dd.description}</p>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}

                    {/* TRANSFER TASLAĞI */}
                    {selectedJob.draft_type === 'transfer' && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-gray-500 text-sm">Kaynak Depo:</span>
                            <p className="font-bold text-gray-800">{selectedJob.draft_data.source_branch_name || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-sm">Hedef Depo:</span>
                            <p className="font-bold text-gray-800">{selectedJob.draft_data.destination_branch_name || '-'}</p>
                          </div>
                        </div>
                        
                        {/* Transfer Ürünleri */}
                        {selectedJob.draft_data.items && selectedJob.draft_data.items.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="text-gray-500 text-sm font-medium">Transfer Edilecek Ürünler:</span>
                            <div className="mt-2 border rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-amber-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left">Ürün</th>
                                    <th className="px-3 py-2 text-center">Miktar</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedJob.draft_data.items.map((item, idx) => (
                                    <tr key={idx} className="border-t">
                                      <td className="px-3 py-2">{item.stok_karti_adi || item.product_name || '-'}</td>
                                      <td className="px-3 py-2 text-center">{item.quantity || item.miktar} {item.birim || 'adet'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {selectedJob.draft_data.description && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="text-gray-500 text-sm">Açıklama:</span>
                            <p className="text-gray-800">{selectedJob.draft_data.description}</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* BİLGİLENDİRME TASLAĞI */}
                    {selectedJob.draft_type === 'info' && (
                      <>
                        {selectedJob.draft_data.description && (
                          <div>
                            <span className="text-gray-500 text-sm">Bilgilendirme:</span>
                            <p className="text-gray-800 whitespace-pre-wrap">{selectedJob.draft_data.description}</p>
                          </div>
                        )}
                        
                        {selectedJob.draft_data.documents && selectedJob.draft_data.documents.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="text-gray-500 text-sm">Belgeler:</span>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {selectedJob.draft_data.documents.map((file, idx) => (
                                <a
                                  key={idx}
                                  href={file.data || file.url}
                                  download={file.name}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded text-sm hover:bg-amber-200"
                                >
                                  <Download className="h-3 w-3 text-amber-600" />
                                  {file.name}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  <div className={`mt-3 pt-3 border-t ${selectedJob.status === 'finalized' ? 'border-green-300 text-green-700' : 'border-amber-300 text-amber-700'} text-sm`}>
                    <span>{selectedJob.status === 'finalized' ? 'Onaylayan: ' : 'Hazırlayan: '}</span>
                    <span className="font-semibold">{selectedJob.status === 'finalized' ? (selectedJob.finalized_by_name || selectedJob.draft_created_by_name || '-') : (selectedJob.draft_created_by_name || '-')}</span>
                    {(selectedJob.status === 'finalized' ? selectedJob.finalized_at : selectedJob.draft_created_at) && (
                      <span className="ml-2">({new Date(selectedJob.status === 'finalized' ? selectedJob.finalized_at : selectedJob.draft_created_at).toLocaleString('tr-TR')})</span>
                    )}
                  </div>
                </div>
              )}

              {/* İşlem Butonları */}
              <div className="border-t pt-4 mt-4 flex flex-wrap gap-2">
                {(selectedJob.status === 'active' || selectedJob.status === 'approved' || selectedJob.status === 'prepared') && !selectedJob.selected_teklif && (
                  <>
                    <Button onClick={() => openPrepareRequestModal(selectedJob)} className="bg-purple-600 hover:bg-purple-700">
                      <FileText className="h-4 w-4 mr-2" />
                      Talebi Hazırla
                    </Button>
                  </>
                )}
                {selectedJob.status === 'correction' && (
                  <Button onClick={() => openCorrectionModal(selectedJob)} className="bg-orange-600 hover:bg-orange-700">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Düzeltme Gönder
                  </Button>
                )}
                {/* 4 İşlem Butonu - Sadece Yönetim Onayladı durumundaki işlerde göster */}
                {selectedJob.status === 'approved_by_management' && (
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => {
                        // Yeni Sipariş - localStorage'a kaydet ve yönlendir
                        localStorage.setItem('draft_source_job_id', selectedJob.id);
                        localStorage.setItem('draft_source_job_name', selectedJob.amac || selectedJob.konu || '');
                        // Seçilen teklifin offer_id'sini çöz (prepared_request'ten)
                        let selectedOfferId = selectedJob.selected_teklif?.offer_id || null;
                        const prTeklifler = selectedJob.prepared_request?.teklifler || [];
                        if (!selectedOfferId && prTeklifler.length > 0) {
                          let idx = -1;
                          if (selectedJob.selected_teklif_id && String(selectedJob.selected_teklif_id).startsWith('teklif_')) {
                            idx = parseInt(String(selectedJob.selected_teklif_id).replace('teklif_', ''), 10);
                          }
                          if (idx >= 0 && idx < prTeklifler.length) {
                            selectedOfferId = prTeklifler[idx].offer_id || null;
                          } else if (selectedJob.selected_teklif) {
                            const m = prTeklifler.find(t => t.firma_adi === selectedJob.selected_teklif.firma_adi && t.toplam_fiyat === selectedJob.selected_teklif.toplam_fiyat);
                            if (m) selectedOfferId = m.offer_id || null;
                          }
                        }
                        // Seçilen teklif ve iş bilgilerini de kaydet
                        const jobDataForOrder = {
                          job_id: selectedJob.id,
                          job_name: selectedJob.amac || selectedJob.konu || '',
                          branch_id: selectedJob.branch_id || selectedJob.sube_id || '',
                          branch_name: selectedJob.branch_name || '',
                          selected_teklif: selectedJob.selected_teklif || null,
                          selected_offer_id: selectedOfferId,
                          teklifler: prTeklifler
                        };
                        localStorage.setItem('draft_source_job_data', JSON.stringify(jobDataForOrder));
                        setDetailModalOpen(false);
                        navigate('/purchasing/purchased?openAddModal=true&jobId=' + selectedJob.id + '&jobName=' + encodeURIComponent(selectedJob.amac || selectedJob.konu || ''));
                      }} 
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Yeni Sipariş
                    </Button>
                    <Button 
                      onClick={() => {
                        // Yeni Demirbaş - localStorage'a kaydet ve yönlendir
                        localStorage.setItem('draft_source_job_id', selectedJob.id);
                        localStorage.setItem('draft_source_job_name', selectedJob.amac || selectedJob.konu || '');
                        // Seçilen teklifin offer_id'sini çöz (prepared_request'ten)
                        let selectedOfferId = selectedJob.selected_teklif?.offer_id || null;
                        const prTeklifler = selectedJob.prepared_request?.teklifler || [];
                        if (!selectedOfferId && prTeklifler.length > 0) {
                          let idx = -1;
                          if (selectedJob.selected_teklif_id && String(selectedJob.selected_teklif_id).startsWith('teklif_')) {
                            idx = parseInt(String(selectedJob.selected_teklif_id).replace('teklif_', ''), 10);
                          }
                          if (idx >= 0 && idx < prTeklifler.length) {
                            selectedOfferId = prTeklifler[idx].offer_id || null;
                          } else if (selectedJob.selected_teklif) {
                            const m = prTeklifler.find(t => t.firma_adi === selectedJob.selected_teklif.firma_adi && t.toplam_fiyat === selectedJob.selected_teklif.toplam_fiyat);
                            if (m) selectedOfferId = m.offer_id || null;
                          }
                        }
                        const jobDataForOrder = {
                          job_id: selectedJob.id,
                          job_name: selectedJob.amac || selectedJob.konu || '',
                          branch_id: selectedJob.branch_id || selectedJob.sube_id || '',
                          branch_name: selectedJob.branch_name || '',
                          selected_teklif: selectedJob.selected_teklif || null,
                          selected_offer_id: selectedOfferId,
                          teklifler: prTeklifler
                        };
                        localStorage.setItem('draft_source_job_data', JSON.stringify(jobDataForOrder));
                        setDetailModalOpen(false);
                        navigate('/purchasing/demirbaslar?openAddModal=true&jobId=' + selectedJob.id + '&jobName=' + encodeURIComponent(selectedJob.amac || selectedJob.konu || ''));
                      }} 
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <PackagePlus className="h-4 w-4 mr-2" />
                      Yeni Demirbaş
                    </Button>
                    <Button 
                      onClick={() => {
                        // Depolar Arası Transfer - localStorage'a kaydet ve yönlendir
                        localStorage.setItem('draft_source_job_id', selectedJob.id);
                        localStorage.setItem('draft_source_job_name', selectedJob.amac || selectedJob.konu || '');
                        setDetailModalOpen(false);
                        navigate('/purchasing/warehouses?openTransferModal=true&jobId=' + selectedJob.id + '&jobName=' + encodeURIComponent(selectedJob.amac || selectedJob.konu || ''));
                      }} 
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <ArrowLeftRight className="h-4 w-4 mr-2" />
                      Depolar Arası Transfer
                    </Button>
                    <Button 
                      onClick={() => {
                        // Bilgilendirme - Modalı aç
                        setInfoJob(selectedJob);
                        setInfoDescription('');
                        setInfoFiles([]);
                        setInfoModalOpen(true);
                      }} 
                      variant="outline"
                      className="border-purple-500 text-purple-600 hover:bg-purple-50"
                    >
                      <Info className="h-4 w-4 mr-2" />
                      Bilgilendirme
                    </Button>
                    <Button 
                      onClick={() => {
                        // Yeni Hizmet Alımı - Modalı aç
                        setNewServiceJob(selectedJob);
                        resetNewServiceData();
                        // İş bilgilerini varsayılan olarak doldur
                        setNewServiceData(prev => ({
                          ...prev,
                          branch_id: selectedJob.branch_id || '',
                          branch_name: selectedJob.branch_name || ''
                        }));
                        setShowNewServiceModal(true);
                      }} 
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Wrench className="h-4 w-4 mr-2" />
                      Yeni Hizmet Alımı
                    </Button>
                  </div>
                )}
                {/* Hazırlanan işlerde Onayla ve Tamamla butonu - SADECE YÖNETİM PANELİNDE GÖSTERİLİR */}
                {/* Satın Alma Panelinde bu buton görünmez */}
                
                {/* Tamamlanan işlerde Muhasebeye İlet butonu - SADECE YÖNETİM PANELİNDE GÖSTERİLİR */}
                {/* Satın Alma Panelinde bu buton görünmez */}
                
                {/* Zaten muhasebeye iletilmişse bilgi göster */}
                {selectedJob.forwarded_to_accounting && (
                  <Badge className="bg-purple-100 text-purple-700 py-2 px-3">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Muhasebeye İletildi ({selectedJob.forwarded_to_accounting_at ? new Date(selectedJob.forwarded_to_accounting_at).toLocaleDateString('tr-TR') : ''})
                  </Badge>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between items-center">
            <div className="flex gap-2">
              {/* Board panelinde Onay Bekleyen işler için Onayla/Reddet */}
              {isBoard && selectedJob?.status === 'pending' && (
                <>
                  <Button 
                    onClick={() => handleApprove(selectedJob)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Onayla
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => handleReject(selectedJob)}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reddet
                  </Button>
                  <Button 
                    onClick={async () => {
                      if (!window.confirm('Bu işi satın alma süreci olmadan direkt onaylamak istediğinizden emin misiniz?')) return;
                      try {
                        const token = localStorage.getItem('fortis_token');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/board/purchasing-jobs/${selectedJob.id}/approve-without-purchasing`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({ approval_note: 'Satın alma dışı onaylandı' })
                        });
                        
                        if (response.ok) {
                          toast.success('İş satın alma dışı onaylandı');
                          setDetailModalOpen(false);
                          fetchJobs();
                        } else {
                          const err = await response.json();
                          toast.error(err.detail || 'Onaylama başarısız');
                        }
                      } catch (error) {
                        console.error('Onaylama hatası:', error);
                        toast.error('Onaylama başarısız');
                      }
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="approve-without-purchasing-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Satın Alma Dışı Onayla
                  </Button>
                </>
              )}
              {/* Board panelinde Yönetime İletilen işler için Onayla/Düzelt */}
              {isBoard && (selectedJob?.status === 'forwarded_to_management' || selectedJob?.status === 'pending_management') && (
                <>
                  <Button 
                    onClick={() => { setDetailModalOpen(false); handleManagementApprove(selectedJob); }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Yönetim Onayı Ver
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => { setDetailModalOpen(false); setManagementCorrectionModalOpen(true); }}
                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Düzeltme İste
                  </Button>
                </>
              )}
              
              {/* Yönetim Kurulu Paneli'nde Hazırlanan İşler için Onayla/Reddet/Düzelt butonları */}
              {isBoard && selectedJob?.status === 'completed' && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setRejectionReason('');
                      setRejectModalOpen(true);
                    }}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reddet
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setCorrectionNote('');
                      setCorrectionModalOpen(true);
                    }}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Düzeltme İste
                  </Button>
                  <Button 
                    onClick={() => handleFinalize(selectedJob)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Onayla ve Tamamla
                  </Button>
                </>
              )}
              
              {/* Satın Alma Paneli'nde Hazırlanan İşlerde buton yok - Onay Yönetim Kurulu'nda yapılır */}
              
              {/* Satın Alma Paneli'nde Onay Bekleyen işler için İptal Et butonu */}
              {!isBoard && selectedJob?.status === 'pending' && (
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setCancelReason('');
                    setCancelModalOpen(true);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  İptal Et
                </Button>
              )}

              {/* Tamamlanan işlerde Arşive Ekle butonu */}
              {selectedJob?.status === 'finalized' && (
                <Button
                  onClick={() => handleArchiveJob(selectedJob)}
                  className="bg-gray-700 hover:bg-gray-800"
                  data-testid="archive-job-btn"
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arşive Ekle
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İş Ekleme Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-orange-600" />
              Yeni İş Ekle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Konu *</Label>
              <Input
                value={formData.konu}
                onChange={(e) => setFormData(prev => ({ ...prev, konu: e.target.value }))}
                placeholder="İşin konusu..."
              />
            </div>
            
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={formData.aciklama}
                onChange={(e) => setFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="İş hakkında detaylar..."
                rows={4}
              />
            </div>
            
            <div>
              <Label>Şube</Label>
              <Select
                value={formData.sube_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, sube_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Şube seçin" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Belgeler</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center cursor-pointer text-gray-500 hover:text-gray-700"
                >
                  <Upload className="h-8 w-8 mb-2" />
                  <span className="text-sm">Dosya yüklemek için tıklayın</span>
                </label>
              </div>
              {formData.belgeler.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.belgeler.map((file, index) => (
                    <div key={index} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm">
                      <File className="h-3 w-3" />
                      {file.name}
                      <button onClick={() => removeFile(index)} className="ml-1 text-red-500 hover:text-red-700">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-orange-600 hover:bg-orange-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Düzeltme Gönderme Modal */}
      <Dialog open={correctionModalOpen} onOpenChange={setCorrectionModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Düzeltme Gönder
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Konu *</Label>
              <Input
                value={correctionFormData.konu}
                onChange={(e) => setCorrectionFormData(prev => ({ ...prev, konu: e.target.value }))}
                placeholder="İşin konusu..."
              />
            </div>
            
            <div>
              <Label>Düzeltme Açıklaması</Label>
              <Textarea
                value={correctionFormData.aciklama}
                onChange={(e) => setCorrectionFormData(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="Yapılan düzeltmeler hakkında bilgi..."
                rows={4}
              />
            </div>
            
            <div>
              <Label>Belgeler</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleCorrectionFileUpload}
                  className="hidden"
                  id="correction-file-upload"
                />
                <label
                  htmlFor="correction-file-upload"
                  className="flex flex-col items-center cursor-pointer text-gray-500 hover:text-gray-700"
                >
                  <Upload className="h-8 w-8 mb-2" />
                  <span className="text-sm">Dosya yüklemek için tıklayın</span>
                </label>
              </div>
              {correctionFormData.belgeler.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {correctionFormData.belgeler.map((file, index) => (
                    <div key={index} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm">
                      <File className="h-3 w-3" />
                      {file.name}
                      <button onClick={() => removeCorrectionFile(index)} className="ml-1 text-red-500 hover:text-red-700">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionModalOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleSubmitCorrection} disabled={correctionSubmitting} className="bg-orange-600 hover:bg-orange-700">
              {correctionSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yönetim Düzeltme Modal */}
      <Dialog open={managementCorrectionModalOpen} onOpenChange={setManagementCorrectionModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-orange-600" />
              Düzeltme İste
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>İş</Label>
              <p className="text-sm text-gray-600 mt-1">{selectedJob?.konu || selectedJob?.title}</p>
            </div>
            
            <div>
              <Label>Düzeltme Notu *</Label>
              <Textarea
                value={correctionNote}
                onChange={(e) => setCorrectionNote(e.target.value)}
                placeholder="Düzeltilmesi gereken konuları yazın..."
                rows={4}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setManagementCorrectionModalOpen(false); setCorrectionNote(''); }}>
              İptal
            </Button>
            <Button onClick={handleManagementCorrection} className="bg-orange-600 hover:bg-orange-700">
              <Send className="h-4 w-4 mr-2" />
              Düzeltme İste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Talebi Hazırla Modal */}
      <Dialog open={prepareRequestModalOpen} onOpenChange={setPrepareRequestModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Talebi Hazırla
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* İş Türü */}
            <div>
              <Label className="text-base font-semibold">İş Türü *</Label>
              <Select
                value={prepareRequestData.job_type}
                onValueChange={(value) => setPrepareRequestData(prev => ({ ...prev, job_type: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="job-type-select">
                  <SelectValue placeholder="İş türü seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urun_satin_alma">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-blue-600" />
                      Ürün Satın Alma
                    </div>
                  </SelectItem>
                  <SelectItem value="hizmet_satin_alma">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-green-600" />
                      Hizmet Satın Alma
                    </div>
                  </SelectItem>
                  <SelectItem value="diger">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-600" />
                      Diğer
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tarih ve Saat */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={prepareRequestData.tarih}
                  onChange={(e) => setPrepareRequestData(prev => ({ ...prev, tarih: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Saat</Label>
                <Input
                  type="time"
                  value={prepareRequestData.saat}
                  onChange={(e) => setPrepareRequestData(prev => ({ ...prev, saat: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            {/* İşin Adı */}
            <div>
              <Label>İşin Adı</Label>
              <Input
                value={prepareRequestData.is_adi}
                onChange={(e) => setPrepareRequestData(prev => ({ ...prev, is_adi: e.target.value }))}
                placeholder="İşin adı"
                className="mt-1"
              />
            </div>

            {/* Açıklama */}
            <div>
              <Label>Açıklama</Label>
              <Textarea
                value={prepareRequestData.aciklama}
                onChange={(e) => setPrepareRequestData(prev => ({ ...prev, aciklama: e.target.value }))}
                placeholder="Açıklama"
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Teklifler */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-lg font-semibold">Teklifler</Label>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    onClick={() => setShowOfferSelectModal(true)} 
                    variant="outline" 
                    className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="add-from-existing-offer-btn"
                  >
                    <FileText className="h-4 w-4" />
                    Mevcut Tekliften Ekle
                  </Button>
                  <Button 
                    type="button" 
                    onClick={addTeklif} 
                    variant="outline" 
                    className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="add-manual-offer-btn"
                  >
                    <Plus className="h-4 w-4" />
                    Manuel Ekle
                  </Button>
                </div>
              </div>

              {prepareRequestData.teklifler.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                  <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Henüz teklif eklenmedi</p>
                  <p className="text-sm">"Mevcut Tekliften Ekle" veya "Manuel Ekle" butonuna tıklayın</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {prepareRequestData.teklifler.map((teklif, index) => (
                    <div key={teklif.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-purple-700">
                            {index + 1}. Teklif
                          </h4>
                          {teklif.from_existing && teklif.offer_no && (
                            <Badge className="bg-orange-100 text-orange-700 text-xs">
                              {teklif.offer_no}
                            </Badge>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTeklif(teklif.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-sm">Firma Adı *</Label>
                          <Input
                            value={teklif.firma_adi}
                            onChange={(e) => updateTeklif(teklif.id, 'firma_adi', e.target.value)}
                            placeholder="Firma adı"
                            className="mt-1"
                            disabled={teklif.from_existing}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Toplam Fiyat *</Label>
                          <Input
                            value={teklif.toplam_fiyat}
                            onChange={(e) => updateTeklif(teklif.id, 'toplam_fiyat', e.target.value)}
                            placeholder="Örn: 15.000 TL"
                            className="mt-1"
                            disabled={teklif.from_existing}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Ödeme Planı</Label>
                          <Textarea
                            value={teklif.odeme_plani}
                            onChange={(e) => updateTeklif(teklif.id, 'odeme_plani', e.target.value)}
                            placeholder="Örn: Peşin / 3 Taksit"
                            className="mt-1 text-sm"
                            rows={teklif.from_existing && teklif.odeme_plani?.includes('\n') ? 4 : 1}
                            disabled={teklif.from_existing}
                          />
                        </div>
                      </div>
                      
                      {/* Teklif Açıklaması */}
                      <div className="mt-3">
                        <Label className="text-sm">Açıklama</Label>
                        <Textarea
                          value={teklif.aciklama || ''}
                          onChange={(e) => updateTeklif(teklif.id, 'aciklama', e.target.value)}
                          placeholder="Teklif hakkında notlar, özel koşullar vb."
                          className="mt-1"
                          rows={teklif.from_existing && teklif.aciklama?.includes('\n') ? 8 : 2}
                          disabled={teklif.from_existing}
                        />
                      </div>

                      {/* Teklif Belgeleri */}
                      <div className="mt-3">
                        <Label className="text-sm">Belgeler</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="file"
                            multiple
                            onChange={(e) => handleTeklifFileUpload(e, teklif.id)}
                            className="text-sm"
                          />
                        </div>
                        {teklif.belgeler.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {teklif.belgeler.map((file, fileIndex) => (
                              <div key={fileIndex} className="flex items-center gap-1 px-2 py-1 bg-white border rounded text-sm">
                                <File className="h-3 w-3 text-gray-500" />
                                {file.name}
                                <button
                                  type="button"
                                  onClick={() => removeTeklifFile(teklif.id, fileIndex)}
                                  className="ml-1 text-red-500 hover:text-red-700"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="mt-6 flex gap-2">
            <Button variant="outline" onClick={() => setPrepareRequestModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSavePrepareRequestDraft} 
              disabled={prepareRequestSubmitting} 
              variant="outline"
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              {prepareRequestSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Kaydet
            </Button>
            <Button 
              onClick={handleSubmitPrepareRequest} 
              disabled={prepareRequestSubmitting || prepareRequestData.teklifler.length === 0} 
              className="bg-purple-600 hover:bg-purple-700"
            >
              {prepareRequestSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Yönetime İlet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Atanan İş Detay Modal */}
      <Dialog open={assignedDetailModalOpen} onOpenChange={setAssignedDetailModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              {selectedAssignedJob?.name || selectedAssignedJob?.title || 'İş Detayı'}
            </DialogTitle>
          </DialogHeader>
          {selectedAssignedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Şirket</p>
                  <p className="font-medium">{selectedAssignedJob.company_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">İlerleme</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${selectedAssignedJob.progress || 0}%` }} />
                    </div>
                    <span className="text-sm font-medium">{selectedAssignedJob.progress || 0}%</span>
                  </div>
                </div>
              </div>
              {selectedAssignedJob.description && (
                <div>
                  <p className="text-sm text-gray-500">Açıklama</p>
                  <p className="text-sm">{selectedAssignedJob.description}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-2">Takip Edenler</p>
                <div className="flex flex-wrap gap-2">
                  {(selectedAssignedJob.tracking_user_details || []).map((user, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-purple-100 text-purple-700">
                      {user.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2 sm:gap-2">
            {selectedAssignedJob?.status !== 'completed' && selectedAssignedJob?.status !== 'rejected' && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => setRejectModalOpen(true)}
                  className="gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Reddet
                </Button>
                <Button 
                  onClick={() => setCompleteModalOpen(true)}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle className="h-4 w-4" />
                  Tamamla
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setAssignedDetailModalOpen(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tamamlama Modal */}
      <Dialog open={completeModalOpen} onOpenChange={setCompleteModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <CheckCircle className="h-5 w-5" />
              İşi Tamamla
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              "{selectedAssignedJob?.name || selectedAssignedJob?.title}" işini tamamlamak istediğinize emin misiniz?
            </p>
            <div className="space-y-2">
              <Label>Tamamlama Notu (İsteğe bağlı)</Label>
              <Textarea
                placeholder="İş hakkında not ekleyin..."
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteModalOpen(false)}>
              İptal
            </Button>
            <Button onClick={handleCompleteAssignedJob} disabled={completeLoading} className="bg-blue-600 hover:bg-blue-700">
              {completeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Tamamla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reddetme Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              İşi Reddet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              "{selectedAssignedJob?.name || selectedAssignedJob?.title}" işini reddetmek istediğinize emin misiniz?
            </p>
            <div className="space-y-2">
              <Label>Red Sebebi <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="Reddetme sebebini yazın..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleRejectAssignedJob} disabled={rejectLoading}>
              {rejectLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Reddet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yönetim Kurulu Onay Modalı */}
      <Dialog open={approvalModalOpen} onOpenChange={setApprovalModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              İş Onayı - Yönetim Kurulu
            </DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-6">
              {/* İş Detayları */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 text-gray-700">İş Bilgileri</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Konu:</span>
                    <p className="font-medium">{selectedJob.konu}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Şube:</span>
                    <p className="font-medium">{selectedJob.branch_name || '-'}</p>
                  </div>
                  {selectedJob.prepared_request?.aciklama && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Açıklama:</span>
                      <p className="font-medium">{selectedJob.prepared_request.aciklama}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Teklifler */}
              {selectedJob.prepared_request?.teklifler && selectedJob.prepared_request.teklifler.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Teklifler ({selectedJob.prepared_request.teklifler.length})
                    <span className="text-red-500 text-sm">*Bir teklif seçin</span>
                  </h4>
                  <div className="space-y-3">
                    {selectedJob.prepared_request.teklifler.map((teklif, index) => {
                      const teklifKey = teklif.id || `teklif_${index}`;
                      const isSelected = approvalData.selectedTeklifId === teklifKey || approvalData.selectedTeklifIndex === index;
                      return (
                      <div 
                        key={teklifKey}
                        className={`border rounded-lg p-3 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-200' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => setApprovalData(prev => ({ 
                          ...prev, 
                          selectedTeklifId: teklifKey,
                          selectedTeklifIndex: index,
                          selectedTeklif: teklif
                        }))}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <input 
                                type="radio" 
                                name="teklif_selection"
                                checked={isSelected}
                                onChange={() => setApprovalData(prev => ({ 
                                  ...prev, 
                                  selectedTeklifId: teklifKey,
                                  selectedTeklifIndex: index,
                                  selectedTeklif: teklif
                                }))}
                                className="h-4 w-4 text-green-600"
                              />
                              <span className="font-semibold text-gray-800">
                                {teklif.firma_adi || `Teklif ${index + 1}`}
                              </span>
                            </div>
                            <div className="ml-6 mt-1 text-sm text-gray-600">
                              <p><strong>Fiyat:</strong> {teklif.fiyat ? `${Number(teklif.fiyat).toLocaleString('tr-TR')} ₺` : (teklif.toplam_fiyat || '-')}</p>
                              {teklif.aciklama && <p><strong>Açıklama:</strong> {teklif.aciklama}</p>}
                              {teklif.odeme_plani && <p><strong>Ödeme Planı:</strong> {teklif.odeme_plani}</p>}
                            </div>
                          </div>
                          {(teklif.belge_url || (teklif.belgeler && teklif.belgeler.length > 0)) && (
                            <div className="flex flex-col gap-1">
                              {teklif.belge_url && (
                                <a 
                                  href={teklif.belge_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Download className="h-4 w-4" />
                                  Teklif Belgesi
                                </a>
                              )}
                              {teklif.belgeler && teklif.belgeler.map((belge, bIdx) => (
                                <a 
                                  key={bIdx}
                                  href={belge.url || belge.data} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Download className="h-4 w-4" />
                                  {belge.name || `Belge ${bIdx + 1}`}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Onay Açıklaması
                </label>
                <textarea
                  value={approvalData.aciklama}
                  onChange={(e) => setApprovalData(prev => ({ ...prev, aciklama: e.target.value }))}
                  placeholder="Onay ile ilgili notlarınızı yazın..."
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows={3}
                />
              </div>

              {/* Belge Yükleme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ek Belgeler (Opsiyonel)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setApprovalData(prev => ({ ...prev, belgeler: [...prev.belgeler, ...files] }));
                    }}
                    className="hidden"
                    id="approval-file-input"
                  />
                  <label 
                    htmlFor="approval-file-input" 
                    className="flex flex-col items-center cursor-pointer"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-500 mt-2">Belge eklemek için tıklayın</span>
                  </label>
                  
                  {approvalData.belgeler.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {approvalData.belgeler.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-600 flex items-center gap-2">
                            <File className="h-4 w-4" />
                            {file.name}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setApprovalData(prev => ({
                              ...prev,
                              belgeler: prev.belgeler.filter((_, i) => i !== index)
                            }))}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setApprovalModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleApprovalSubmit} 
              disabled={approvalSubmitting || (selectedJob?.prepared_request?.teklifler?.length > 0 && !approvalData.selectedTeklifId && approvalData.selectedTeklifIndex === null)}
              className="bg-green-600 hover:bg-green-700"
            >
              {approvalSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Onayla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Muhasebeye İlet Modal */}
      <Dialog open={forwardToAccountingModalOpen} onOpenChange={setForwardToAccountingModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-purple-600" />
              Muhasebeye İlet
            </DialogTitle>
          </DialogHeader>
          
          {forwardToAccountingJob && (
            <div className="space-y-4">
              {/* İş Özeti */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 mb-2">{forwardToAccountingJob.konu}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><span className="font-medium">Şube:</span> {forwardToAccountingJob.branch_name || '-'}</p>
                  {forwardToAccountingJob.selected_teklif && (
                    <>
                      <p><span className="font-medium">Seçilen Firma:</span> {forwardToAccountingJob.selected_teklif.firma_adi}</p>
                      <p><span className="font-medium">Toplam Tutar:</span> <span className="text-green-700 font-semibold">{forwardToAccountingJob.selected_teklif.toplam_fiyat}</span></p>
                    </>
                  )}
                  <p><span className="font-medium">Tamamlanma Tarihi:</span> {forwardToAccountingJob.completed_at ? new Date(forwardToAccountingJob.completed_at).toLocaleDateString('tr-TR') : '-'}</p>
                </div>
              </div>

              {/* Açıklama Alanı */}
              <div>
                <Label>Muhasebe İçin Not (Opsiyonel)</Label>
                <Textarea
                  value={forwardToAccountingNote}
                  onChange={(e) => setForwardToAccountingNote(e.target.value)}
                  placeholder="Muhasebe departmanına iletmek istediğiniz notlar..."
                  rows={4}
                  className="mt-1"
                  data-testid="forward-accounting-note"
                />
              </div>

              {/* Bilgi Notu */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Not:</p>
                    <p>Bu işlem, seçilen teklif ve tüm ilgili belgeleri muhasebe departmanına iletecektir. İş fatura işlemleri için hazır hale gelecektir.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setForwardToAccountingModalOpen(false);
                setForwardToAccountingJob(null);
                setForwardToAccountingNote('');
              }}
            >
              İptal
            </Button>
            <Button 
              onClick={handleForwardToAccounting}
              disabled={forwardToAccountingSubmitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="forward-accounting-submit"
            >
              {forwardToAccountingSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Muhasebeye İlet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bilgilendirme Modalı */}
      <Dialog open={infoModalOpen} onOpenChange={setInfoModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-purple-600" />
              Bilgilendirme Gönder
            </DialogTitle>
          </DialogHeader>
          
          {infoJob && (
            <div className="space-y-4 py-4">
              {/* İş Bilgisi */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <Label className="text-purple-700 font-medium">İş Konusu</Label>
                <p className="mt-1 text-gray-800 font-medium">
                  {infoJob.amac || infoJob.konu || infoJob.source_branch_job?.name || '-'}
                </p>
                {infoJob.seri_no && (
                  <p className="text-sm text-purple-600 mt-1">Seri No: {infoJob.seri_no}</p>
                )}
              </div>
              
              {/* Açıklama */}
              <div>
                <Label className="text-gray-700">Açıklama</Label>
                <Textarea
                  placeholder="Bilgilendirme açıklamasını yazın..."
                  value={infoDescription}
                  onChange={(e) => setInfoDescription(e.target.value)}
                  rows={4}
                  className="mt-1"
                />
              </div>
              
              {/* Belge Ekleme */}
              <div>
                <Label className="text-gray-700">Belgeler</Label>
                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      setInfoFiles(prev => [...prev, ...files]);
                    }}
                    className="hidden"
                    id="info-file-upload"
                  />
                  <label 
                    htmlFor="info-file-upload"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Belge yüklemek için tıklayın</span>
                    <span className="text-xs text-gray-400 mt-1">PDF, Word, Excel, resim dosyaları</span>
                  </label>
                </div>
                
                {/* Yüklenen Belgeler */}
                {infoFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {infoFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setInfoFiles(prev => prev.filter((_, i) => i !== index))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setInfoModalOpen(false)}
              disabled={infoSubmitting}
            >
              İptal
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={async () => {
                if (!infoDescription.trim()) {
                  toast.error('Lütfen açıklama yazın');
                  return;
                }
                
                setInfoSubmitting(true);
                try {
                  // Belgeleri yükle
                  let uploadedFiles = [];
                  if (infoFiles.length > 0) {
                    for (const file of infoFiles) {
                      const formData = new FormData();
                      formData.append('file', file);
                      const uploadRes = await axios.post(`${API}/upload`, formData, {
                        headers: { 
                          'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
                          'Content-Type': 'multipart/form-data'
                        }
                      });
                      if (uploadRes.data) {
                        uploadedFiles.push(uploadRes.data);
                      }
                    }
                  }
                  
                  // Bilgilendirme taslağı olarak kaydet (Yönetim Onayladı durumundaysa)
                  if (infoJob.status === 'approved_by_management') {
                    try {
                      await axios.post(`${API}/api/purchasing/jobs/${infoJob.id}/save-draft`, {
                        draft_type: 'info',
                        draft_data: {
                          description: infoDescription,
                          documents: uploadedFiles
                        }
                      }, { headers: getAuthHeaders() });
                      
                      toast.success('Bilgilendirme taslağı kaydedildi, yönetim onayı bekleniyor');
                      loadApprovedByManagementJobs();
                      loadCompletedJobs();
                    } catch (markError) {
                      console.error('Taslak kaydetme hatası:', markError);
                      toast.error('Taslak kaydetme başarısız');
                    }
                  } else {
                    // Normal akış - direkt bilgilendirme kaydı oluştur
                    await axios.post(`${API}/purchasing/jobs/${infoJob.id}/info`, {
                      description: infoDescription,
                      documents: uploadedFiles
                    }, { headers: getAuthHeaders() });
                    toast.success('Bilgilendirme başarıyla gönderildi');
                  }
                  
                  setInfoModalOpen(false);
                  setInfoJob(null);
                  setInfoDescription('');
                  setInfoFiles([]);
                } catch (error) {
                  console.error('Bilgilendirme hatası:', error);
                  toast.error('Bilgilendirme gönderilirken hata oluştu');
                } finally {
                  setInfoSubmitting(false);
                }
              }}
              disabled={infoSubmitting || !infoDescription.trim()}
            >
              {infoSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İptal Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="h-5 w-5" />
              İşi İptal Et
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                <strong>{selectedJob?.konu || selectedJob?.amac}</strong> işini iptal etmek üzeresiniz.
              </p>
            </div>
            
            <div>
              <Label className="text-gray-700">İptal Açıklaması <span className="text-red-500">*</span></Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="İptal nedenini açıklayın..."
                className="mt-1"
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">Bu açıklama iş geçmişinde kaydedilecektir.</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              Vazgeç
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelJob}
              disabled={!cancelReason.trim() || cancelSubmitting}
            >
              {cancelSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  İptal Ediliyor...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  İptal Et
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mevcut Tekliften Seçme Modal */}
      <Dialog open={showOfferSelectModal} onOpenChange={setShowOfferSelectModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              Mevcut Tekliften Ekle
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Teklif no veya firma adı ile ara... (örn: TEK-0001)"
                value={offerSearchTerm}
                onChange={(e) => setOfferSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="offer-search-input"
              />
            </div>
            
            {/* Teklif Listesi */}
            {filteredAvailableOffers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>{offerSearchTerm ? 'Arama sonucu bulunamadı' : 'Henüz teklif bulunmuyor'}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredAvailableOffers.map((offer) => {
                  // Toplam fiyatı hesapla
                  const itemsTotal = (offer.items || []).reduce((sum, item) => sum + (item.quantity * item.price), 0);
                  const servicesTotal = (offer.services || []).reduce((sum, srv) => sum + (srv.quantity * srv.price), 0);
                  const totalPrice = itemsTotal + servicesTotal;
                  const currencySymbols = { 'TL': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£' };
                  const symbol = currencySymbols[offer.currency] || '₺';
                  
                  // Ödeme planı gösterimi
                  let paymentLabel = '';
                  let paymentClass = 'text-gray-400';
                  if (offer.payment_type === 'pesin') {
                    paymentLabel = '💵 Peşin';
                    paymentClass = 'text-green-600';
                  } else if (offer.payment_type === 'cek') {
                    paymentLabel = `📝 Çek (${offer.check_count || 0})`;
                    paymentClass = 'text-blue-600';
                  } else if (offer.payment_type === 'vadeli') {
                    paymentLabel = `📅 Vadeli (${offer.installment_count || 0} taksit)`;
                    paymentClass = 'text-purple-600';
                  } else {
                    paymentLabel = '⚠️ Ödeme planı yok';
                    paymentClass = 'text-orange-500';
                  }
                  
                  return (
                    <div 
                      key={offer.id} 
                      className="border rounded-lg p-4 hover:bg-orange-50 cursor-pointer transition-colors"
                      onClick={() => addTeklifFromExisting(offer)}
                      data-testid={`select-offer-${offer.offer_no}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-orange-100 text-orange-700 font-mono">
                            {offer.offer_no}
                          </Badge>
                          <div>
                            <p className="font-medium">{offer.cari_hesap_name}</p>
                            <p className="text-sm text-gray-500">
                              {offer.offer_type === 'urun' ? 'Ürün' : offer.offer_type === 'hizmet' ? 'Hizmet' : offer.offer_type}
                              {offer.branch_name && ` • ${offer.branch_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">
                            {totalPrice.toLocaleString('tr-TR')} {symbol}
                          </p>
                          <p className={`text-xs font-medium ${paymentClass}`}>
                            {paymentLabel}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(offer.offer_date).toLocaleDateString('tr-TR')}
                          </p>
                        </div>
                      </div>
                      {/* Ürün/Hizmet listesi özeti */}
                      {offer.offer_type === 'urun' && offer.items?.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          {offer.items.slice(0, 2).map((item, idx) => (
                            <span key={idx}>
                              {item.product_name} ({item.quantity} {item.unit})
                              {idx < Math.min(offer.items.length - 1, 1) && ', '}
                            </span>
                          ))}
                          {offer.items.length > 2 && <span className="text-gray-400"> +{offer.items.length - 2} ürün daha</span>}
                        </div>
                      )}
                      {offer.offer_type === 'hizmet' && offer.services?.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          {offer.services.slice(0, 2).map((srv, idx) => (
                            <span key={idx}>
                              {srv.service_name} ({srv.quantity} {srv.unit})
                              {idx < Math.min(offer.services.length - 1, 1) && ', '}
                            </span>
                          ))}
                          {offer.services.length > 2 && <span className="text-gray-400"> +{offer.services.length - 2} hizmet daha</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowOfferSelectModal(false); setOfferSearchTerm(''); }}>
              İptal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yeni Hizmet Alımı Modal */}
      <Dialog open={showNewServiceModal} onOpenChange={(open) => { if (!open) { setShowNewServiceModal(false); resetNewServiceData(); setNewServiceJob(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="new-service-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-purple-600" />
              Yeni Hizmet Alımı
              {newServiceJob && (
                <Badge className="bg-orange-100 text-orange-700 ml-2">
                  İş: {newServiceJob.amac || newServiceJob.konu || `#${newServiceJob.id?.substring(0, 8)}`}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Temel Bilgiler */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service_name">Hizmet Adı *</Label>
                <Input
                  id="service_name"
                  placeholder="Hizmet adını girin"
                  value={newServiceData.service_name}
                  onChange={(e) => setNewServiceData(prev => ({ ...prev, service_name: e.target.value }))}
                  data-testid="new-service-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cari_hesap">Firma (Cari Hesap)</Label>
                <Select
                  value={newServiceData.cari_hesap_id}
                  onValueChange={(value) => {
                    const selected = cariHesaplar.find(c => c.id === value);
                    setNewServiceData(prev => ({
                      ...prev,
                      cari_hesap_id: value,
                      cari_hesap_name: selected?.name || ''
                    }));
                  }}
                >
                  <SelectTrigger data-testid="new-service-company-select">
                    <SelectValue placeholder="Firma seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {cariHesaplar.map(cari => (
                      <SelectItem key={cari.id} value={cari.id}>
                        {cari.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="branch">Şube</Label>
                <Select
                  value={newServiceData.branch_id}
                  onValueChange={(value) => {
                    const selected = branches.find(b => b.id === value);
                    setNewServiceData(prev => ({
                      ...prev,
                      branch_id: value,
                      branch_name: selected?.name || ''
                    }));
                  }}
                >
                  <SelectTrigger data-testid="new-service-branch-select">
                    <SelectValue placeholder="Şube seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="total_amount">Toplam Tutar *</Label>
                <div className="flex gap-2">
                  <Input
                    id="total_amount"
                    type="number"
                    placeholder="0.00"
                    value={newServiceData.total_amount}
                    onChange={(e) => setNewServiceData(prev => ({ ...prev, total_amount: e.target.value }))}
                    className="flex-1"
                    data-testid="new-service-amount-input"
                  />
                  <Select
                    value={newServiceData.currency}
                    onValueChange={(value) => setNewServiceData(prev => ({ ...prev, currency: value }))}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TL">₺ TL</SelectItem>
                      <SelectItem value="USD">$ USD</SelectItem>
                      <SelectItem value="EUR">€ EUR</SelectItem>
                      <SelectItem value="GBP">£ GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={newServiceData.start_date}
                  onChange={(e) => setNewServiceData(prev => ({ ...prev, start_date: e.target.value }))}
                  data-testid="new-service-start-date"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date">Bitiş Tarihi</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={newServiceData.end_date}
                  onChange={(e) => setNewServiceData(prev => ({ ...prev, end_date: e.target.value }))}
                  data-testid="new-service-end-date"
                />
              </div>
            </div>

            {/* Açıklama */}
            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                placeholder="Hizmet hakkında açıklama..."
                value={newServiceData.description}
                onChange={(e) => setNewServiceData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="new-service-description"
              />
            </div>

            {/* Sözleşme Belgeleri */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Sözleşme Belgeleri
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => newServiceFileInputRef.current?.click()}
                  data-testid="new-service-upload-contract-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Belge Yükle
                </Button>
                <input
                  ref={newServiceFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleNewServiceFileUpload}
                />
              </div>
              
              {newServiceData.contract_documents.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {newServiceData.contract_documents.map((doc, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                      <File className="h-4 w-4 text-blue-600" />
                      <span className="text-sm truncate max-w-[150px]">{doc.name || doc.filename}</span>
                      <button
                        type="button"
                        onClick={() => removeNewServiceDocument(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Hakedişler */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-lg font-semibold">
                  <Receipt className="h-5 w-5 text-green-600" />
                  Hakediş Bilgileri
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addNewServiceHakedis}
                  data-testid="new-service-add-hakedis-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Hakediş Ekle
                </Button>
              </div>

              {newServiceData.hakedisler.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Henüz hakediş eklenmedi. "Hakediş Ekle" butonuna tıklayarak hakediş ekleyebilirsiniz.
                </p>
              ) : (
                <div className="space-y-4">
                  {newServiceData.hakedisler.map((hakedis, index) => (
                    <div key={hakedis.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-green-100 text-green-700">
                          Hakediş #{hakedis.hakedis_no}
                        </Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeNewServiceHakedis(hakedis.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Tarih</Label>
                          <Input
                            type="date"
                            value={hakedis.tarih}
                            onChange={(e) => updateNewServiceHakedis(hakedis.id, 'tarih', e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Tutar</Label>
                          <div className="flex gap-1">
                            <Input
                              type="number"
                              placeholder="0.00"
                              value={hakedis.tutar}
                              onChange={(e) => updateNewServiceHakedis(hakedis.id, 'tutar', e.target.value)}
                            />
                            <span className="flex items-center px-2 text-sm text-gray-500">
                              {currencySymbols[newServiceData.currency]}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Durum</Label>
                          <Select
                            value={hakedis.durum}
                            onValueChange={(value) => updateNewServiceHakedis(hakedis.id, 'durum', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="beklemede">Beklemede</SelectItem>
                              <SelectItem value="onaylandi">Onaylandı</SelectItem>
                              <SelectItem value="odendi">Ödendi</SelectItem>
                              <SelectItem value="iptal">İptal</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="mt-3 space-y-2">
                        <Label>Açıklama</Label>
                        <Input
                          placeholder="Hakediş açıklaması..."
                          value={hakedis.aciklama}
                          onChange={(e) => updateNewServiceHakedis(hakedis.id, 'aciklama', e.target.value)}
                        />
                      </div>

                      {/* Hakediş Belgeleri */}
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Belgeler</Label>
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) => handleNewServiceHakedisFileUpload(e, hakedis.id)}
                            />
                            <span className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                              <Upload className="h-3 w-3" />
                              Belge Ekle
                            </span>
                          </label>
                        </div>
                        {hakedis.belgeler?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {hakedis.belgeler.map((doc, docIdx) => (
                              <div key={docIdx} className="flex items-center gap-1 bg-white px-2 py-1 rounded border text-sm">
                                <File className="h-3 w-3 text-blue-600" />
                                <span className="truncate max-w-[100px]">{doc.name || doc.filename}</span>
                                <button
                                  type="button"
                                  onClick={() => removeNewServiceHakedisDocument(hakedis.id, docIdx)}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Toplam Hakediş */}
                  {newServiceData.hakedisler.length > 0 && (
                    <div className="flex justify-end">
                      <div className="bg-green-50 px-4 py-2 rounded-lg">
                        <span className="text-sm text-gray-600">Toplam Hakediş: </span>
                        <span className="font-bold text-green-700">
                          {newServiceData.hakedisler.reduce((sum, h) => sum + (parseFloat(h.tutar) || 0), 0).toLocaleString('tr-TR')} {currencySymbols[newServiceData.currency]}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Notlar */}
            <div className="space-y-2">
              <Label htmlFor="notes">Ek Notlar</Label>
              <Textarea
                id="notes"
                placeholder="Varsa ek notlar..."
                value={newServiceData.notes}
                onChange={(e) => setNewServiceData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="new-service-notes"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowNewServiceModal(false); resetNewServiceData(); setNewServiceJob(null); }}
              disabled={newServiceSubmitting}
            >
              İptal
            </Button>
            <Button
              onClick={handleSaveNewService}
              disabled={newServiceSubmitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="new-service-save-btn"
            >
              {newServiceSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Kaydet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teklif Karşılaştırma Modalı (İş Detayından - Gerçek Teklif Verisi) */}
      <Dialog open={showJobCompareModal} onOpenChange={setShowJobCompareModal}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompareArrows className="h-5 w-5 text-indigo-600" />
              Teklif Karşılaştırma ({jobCompareOffers.length} Teklif)
            </DialogTitle>
          </DialogHeader>

          {jobCompareLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : jobCompareOffers.length >= 2 ? (() => {
            const fmtMoney = (val) => typeof val === 'number' ? val.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '-';
            const cSymbols = {'TL':'₺','USD':'$','EUR':'€','GBP':'£'};

            const allProducts = [];
            jobCompareOffers.forEach(o => (o.items || []).forEach(i => { if (!allProducts.includes(i.product_name)) allProducts.push(i.product_name); }));
            const allServices = [];
            jobCompareOffers.forEach(o => (o.services || []).forEach(s => { if (!allServices.includes(s.service_name)) allServices.push(s.service_name); }));

            const subTotal = (o) => (o.items||[]).reduce((s,i)=>s+(i.quantity*i.price),0)+(o.services||[]).reduce((s,v)=>s+(v.quantity*v.price),0);
            const kdvTotal = (o) => (o.items||[]).reduce((s,i)=>s+(i.quantity*i.price*((i.kdv_orani||0)/100)),0)+(o.services||[]).reduce((s,v)=>s+(v.quantity*v.price*((v.kdv_orani||0)/100)),0);
            const grandTotal = (o) => subTotal(o)+kdvTotal(o)+(o.nakliye_var?(parseFloat(o.nakliye_ucreti)||0):0);

            return (
              <div className="space-y-6">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-40 bg-gray-50 font-semibold">Bilgi</TableHead>
                    {jobCompareOffers.map(o => <TableHead key={o.id} className="text-center bg-gray-50"><div className="font-semibold text-orange-600">{o.offer_no}</div></TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    <TableRow><TableCell className="font-medium bg-gray-50">Cari Hesap</TableCell>{jobCompareOffers.map(o => <TableCell key={o.id} className="text-center">{o.cari_hesap_name||'-'}</TableCell>)}</TableRow>
                    <TableRow><TableCell className="font-medium bg-gray-50">Tarih</TableCell>{jobCompareOffers.map(o => <TableCell key={o.id} className="text-center">{o.offer_date?new Date(o.offer_date).toLocaleDateString('tr-TR'):'-'}</TableCell>)}</TableRow>
                    <TableRow><TableCell className="font-medium bg-gray-50">Durum</TableCell>{jobCompareOffers.map(o => <TableCell key={o.id} className="text-center"><Badge variant="outline">{o.status==='secimdeki'?'Seçimdeki':o.status==='kabul_edildi'?'Onaylanan':o.status||'-'}</Badge></TableCell>)}</TableRow>
                    <TableRow><TableCell className="font-medium bg-gray-50">Para Birimi</TableCell>{jobCompareOffers.map(o => <TableCell key={o.id} className="text-center">{o.currency||'TL'}</TableCell>)}</TableRow>
                  </TableBody>
                </Table>

                {allProducts.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold mb-2 flex items-center gap-2"><Package className="h-4 w-4 text-purple-500" />Ürün Karşılaştırması</h3>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="w-40 bg-purple-50 font-semibold">Ürün</TableHead>
                        {jobCompareOffers.map(o => <TableHead key={o.id} className="text-center bg-purple-50"><div className="text-orange-600 font-semibold">{o.offer_no}</div><div className="text-xs text-gray-500">{o.cari_hesap_name}</div></TableHead>)}
                      </TableRow></TableHeader>
                      <TableBody>
                        {allProducts.map(pName => {
                          const items = jobCompareOffers.map(o => (o.items||[]).find(i=>i.product_name===pName));
                          const prices = items.map(i=>i?i.price:Infinity);
                          const min = Math.min(...prices.filter(p=>p!==Infinity));
                          return (
                            <TableRow key={pName}>
                              <TableCell className="font-medium bg-gray-50"><div>{pName}</div>{items.find(i=>i?.model)&&<div className="text-xs text-gray-400">Model: {items.find(i=>i?.model)?.model}</div>}</TableCell>
                              {items.map((item,idx) => {
                                const sym = cSymbols[jobCompareOffers[idx].currency]||'₺';
                                return <TableCell key={jobCompareOffers[idx].id} className="text-center">
                                  {item ? <div className="space-y-1">
                                    <div className="text-sm">{item.quantity} {item.unit}</div>
                                    <div className={`font-semibold ${item.price===min&&prices.filter(p=>p!==Infinity).length>1?'text-green-600':''}`}>{sym}{item.price?.toLocaleString('tr-TR')}{item.price===min&&prices.filter(p=>p!==Infinity).length>1&&' ✓'}</div>
                                    {item.kdv_orani>0&&<div className="text-xs text-gray-400">KDV %{item.kdv_orani}</div>}
                                    <div className="text-xs font-medium text-gray-600">Toplam: {sym}{(item.quantity*item.price*(1+(item.kdv_orani||0)/100)).toLocaleString('tr-TR')}</div>
                                  </div> : <span className="text-gray-300">—</span>}
                                </TableCell>;
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {allServices.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold mb-2 flex items-center gap-2"><Wrench className="h-4 w-4 text-amber-500" />Hizmet Karşılaştırması</h3>
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="w-40 bg-amber-50 font-semibold">Hizmet</TableHead>
                        {jobCompareOffers.map(o => <TableHead key={o.id} className="text-center bg-amber-50"><div className="text-orange-600 font-semibold">{o.offer_no}</div><div className="text-xs text-gray-500">{o.cari_hesap_name}</div></TableHead>)}
                      </TableRow></TableHeader>
                      <TableBody>
                        {allServices.map(sName => {
                          const srvs = jobCompareOffers.map(o => (o.services||[]).find(s=>s.service_name===sName));
                          const prices = srvs.map(s=>s?s.price:Infinity);
                          const min = Math.min(...prices.filter(p=>p!==Infinity));
                          return (
                            <TableRow key={sName}>
                              <TableCell className="font-medium bg-gray-50">{sName}</TableCell>
                              {srvs.map((srv,idx) => {
                                const sym = cSymbols[jobCompareOffers[idx].currency]||'₺';
                                return <TableCell key={jobCompareOffers[idx].id} className="text-center">
                                  {srv ? <div className="space-y-1">
                                    <div className="text-sm">{srv.quantity} {srv.unit}</div>
                                    <div className={`font-semibold ${srv.price===min&&prices.filter(p=>p!==Infinity).length>1?'text-green-600':''}`}>{sym}{srv.price?.toLocaleString('tr-TR')}{srv.price===min&&prices.filter(p=>p!==Infinity).length>1&&' ✓'}</div>
                                    {srv.kdv_orani>0&&<div className="text-xs text-gray-400">KDV %{srv.kdv_orani}</div>}
                                  </div> : <span className="text-gray-300">—</span>}
                                </TableCell>;
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-40 bg-green-50 font-semibold">Toplam</TableHead>
                    {jobCompareOffers.map(o => <TableHead key={o.id} className="text-center bg-green-50"><div className="text-orange-600 font-semibold">{o.offer_no}</div></TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    <TableRow><TableCell className="font-medium bg-gray-50">Ara Toplam</TableCell>{jobCompareOffers.map(o => <TableCell key={o.id} className="text-center">{fmtMoney(subTotal(o))}</TableCell>)}</TableRow>
                    <TableRow><TableCell className="font-medium bg-gray-50">KDV Toplamı</TableCell>{jobCompareOffers.map(o => <TableCell key={o.id} className="text-center">{fmtMoney(kdvTotal(o))}</TableCell>)}</TableRow>
                    <TableRow><TableCell className="font-medium bg-gray-50">Nakliye</TableCell>{jobCompareOffers.map(o => <TableCell key={o.id} className="text-center">{o.nakliye_var?fmtMoney(parseFloat(o.nakliye_ucreti)||0):<span className="text-gray-300">—</span>}</TableCell>)}</TableRow>
                    {(() => {
                      const tots = jobCompareOffers.map(o => grandTotal(o));
                      const minT = Math.min(...tots);
                      return <TableRow className="bg-gray-50 font-bold">
                        <TableCell className="font-bold bg-gray-100">Genel Toplam</TableCell>
                        {jobCompareOffers.map((o,idx) => <TableCell key={o.id} className={`text-center text-lg ${tots[idx]===minT?'text-green-600':''}`}>
                          {fmtMoney(tots[idx])}{tots[idx]===minT&&tots.filter(t=>t!==tots[idx]).length>0&&<Badge className="ml-2 bg-green-100 text-green-700 text-xs">En Uygun</Badge>}
                        </TableCell>)}
                      </TableRow>;
                    })()}
                  </TableBody>
                </Table>
              </div>
            );
          })() : jobCompareOffers.length < 2 && !jobCompareLoading && (
            <div className="text-center py-8 text-gray-500">Karşılaştırma için en az 2 teklif gerekli</div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJobCompareModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasingJobs;

